import { h, render } from 'https://esm.sh/preact';
import htm from 'https://esm.sh/htm';
import { useState, useEffect } from 'https://esm.sh/preact/hooks';

// Initialize htm with Preact
const html = htm.bind(h);

// The campaign that brought this visitor, captured on the landing page by
// attribution.js. location.href almost never still carries it by the time a
// form is submitted.
const attribution = () => {
  try { return window.slxAttribution ? window.slxAttribution.get() : {}; }
  catch (e) { return {}; }
};
// Long-lived first/last touch (90 days) from attribution.js, or null.
const attrTouches = () => {
  try { return window.slxAttribution && window.slxAttribution.attr ? window.slxAttribution.attr() : null; }
  catch (e) { return null; }
};

// ─── Conversion tracking ───────────────────────────────────────────────
// Fires the mid-funnel "measurements submitted" event once per session to
// GA4 (submit_measurements), Google Ads (secondary conversion action), and
// Meta. Only fires when at least one window has a real width AND height, so
// it reflects genuine intent rather than an empty autosave.
const MEASURE_CONVERSION_SEND_TO = 'AW-17965928709/Z_eACKzXg84cEIWi6fZC';

const hasMeasuredWindow = (list) =>
  Array.isArray(list) && list.some((w) => {
    const width = Number(w.width || w.widthTop || w.widthMiddle || w.widthBottom || 0);
    const height = Number(w.height || w.heightMiddle || w.heightLeft || w.heightRight || 0);
    return width > 0 && height > 0;
  });

const metaConsent = () => {
  try {
    const lf = window.slxLeadFlow;
    if (lf && typeof lf.marketingConsent === 'function') return lf.marketingConsent() === true;
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed() === true;
  } catch (e) { return false; }
  return true; // no consent API on the page: unchanged behaviour
};

const trackMeasurementsSubmitted = (list) => {
  try {
    if (!hasMeasuredWindow(list)) return;
    if (sessionStorage.getItem('slx_measure_tracked')) return; // once per session
    sessionStorage.setItem('slx_measure_tracked', '1');

    const count = Array.isArray(list) ? list.length : 0;

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'submit_measurements', { window_count: count });        // → GA4
      window.gtag('event', 'conversion', { send_to: MEASURE_CONVERSION_SEND_TO }); // → Google Ads
    }
    // window.fbq only exists when the Lead Flow widget loaded the pixel, so
    // send to Meta only with the same marketing consent it uses.
    if (typeof window.fbq === 'function' && metaConsent()) {
      window.fbq('trackCustom', 'SubmitMeasurements', { window_count: count });     // → Meta
    }
  } catch (e) {
    console.error('measurements tracking failed', e);
  }
};

// Fires a funnel-step event to GA4 once per session, so each visitor is
// counted once per step. Powers the visitor → measure_started →
// window_completed → submit_measurements funnel. login_prompted sits between
// the last two on the Norwegian market only, where finishing a window still
// sends guests to the login wall — so its count is NO traffic, not a share of
// the whole funnel. No PII, GA4 only.
const trackFunnelEvent = (name) => {
  try {
    const key = 'slx_funnel_' + name;
    if (sessionStorage.getItem(key)) return; // once per session
    sessionStorage.setItem(key, '1');
    if (typeof window.gtag === 'function') {
      window.gtag('event', name);
    }
  } catch (e) {
    console.error('funnel tracking failed', e);
  }
};

// Icons
const RulerIcon = () => html`
  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M51.4752 16.0498L39.9493 4.52602C39.6243 4.20093 39.2384 3.94305 38.8137 3.76711C38.389 3.59117 37.9338 3.50061 37.4741 3.50061C37.0144 3.50061 36.5592 3.59117 36.1346 3.76711C35.7099 3.94305 35.324 4.20093 34.999 4.52602L4.52272 35.0001C4.19763 35.3251 3.93975 35.711 3.76381 36.1357C3.58787 36.5603 3.49731 37.0155 3.49731 37.4752C3.49731 37.9349 3.58787 38.3901 3.76381 38.8148C3.93975 39.2395 4.19763 39.6254 4.52272 39.9504L16.0487 51.4741C16.3737 51.7992 16.7595 52.0571 17.1842 52.2331C17.6089 52.409 18.0641 52.4995 18.5238 52.4995C18.9835 52.4995 18.4387 52.409 19.8634 52.2331C20.2881 52.0571 20.674 51.7992 20.999 51.4741L51.4752 21.0001C51.8003 20.6751 52.0582 20.2892 52.2341 19.8645C52.4101 19.4398 52.5006 18.9846 52.5006 18.5249C52.5006 18.0652 52.4101 17.61 52.2341 17.1853C52.0582 16.7607 51.8003 16.3748 51.4752 16.0498ZM18.5227 49.0001L6.99897 37.4741L13.999 30.4741L19.7608 36.2382C19.9234 36.4008 20.1165 36.5298 20.3289 36.6178C20.5413 36.7058 20.769 36.7511 20.999 36.7511C21.2289 36.7511 21.4566 36.7058 21.669 36.6178C21.8815 36.5298 22.0745 36.4008 22.2371 36.2382C22.3997 36.0756 22.5287 35.8826 22.6167 35.6701C22.7047 35.4577 22.7499 35.23 22.7499 35.0001C22.7499 34.7701 22.7047 34.5424 22.6167 34.33C22.5287 34.1176 22.3997 33.9245 22.2371 33.762L16.473 28.0001L20.999 23.4741L26.7608 29.2382C27.0892 29.5666 27.5346 29.7511 27.999 29.7511C28.4634 29.7511 28.9087 29.5666 29.2371 29.2382C29.5655 28.9098 29.7499 28.4645 29.7499 28.0001C29.7499 27.5357 29.5655 27.0903 29.2371 26.762L23.473 21.0001L27.999 16.4741L33.7608 22.2382C33.9234 22.4008 34.1165 22.5298 34.3289 22.6178C34.5413 22.7058 34.769 22.7511 34.999 22.7511C35.2289 22.7511 35.4566 22.7058 35.669 22.6178C35.8815 22.5298 36.0745 22.4008 36.2371 22.2382C36.3997 22.0756 36.5287 21.8826 36.6167 21.6701C36.7047 21.4577 36.7499 21.23 36.7499 21.0001C36.7499 20.7701 36.7047 20.5424 36.6167 20.33C36.5287 20.1176 36.3997 19.9245 36.2371 19.762L30.473 14.0001L37.473 7.00008L48.999 18.526L18.5227 49.0001Z" fill="#5B5BF9"/>
  </svg>
`;

const EditIcon = () => html`
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
`;

const TrashIcon = () => html`
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
`;

const CopyIcon = () => html`
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
`;

const UploadIcon = () => html`
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
`;

const CheckCircleIcon = () => html`
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.5459 8.95406C16.6508 9.05858 16.734 9.18277 16.7908 9.31952C16.8476 9.45626 16.8768 9.60287 16.8768 9.75094C16.8768 9.899 16.8476 10.0456 16.7908 10.1824C16.734 10.3191 16.6508 10.4433 16.5459 10.5478L11.2959 15.7978C11.1914 15.9027 11.0672 15.9859 10.9305 16.0427C10.7937 16.0995 10.6471 16.1287 10.4991 16.1287C10.351 16.1287 10.2044 16.0995 10.0676 16.0427C9.9309 15.9859 9.80671 15.9027 9.70219 15.7978L7.45219 13.5478C7.34754 13.4432 7.26453 13.3189 7.2079 13.1822C7.15127 13.0455 7.12212 12.8989 7.12212 12.7509C7.12212 12.6029 7.15127 12.4564 7.2079 12.3197C7.26453 12.1829 7.34754 12.0587 7.45219 11.9541C7.55684 11.8494 7.68107 11.7664 7.8178 11.7098C7.95453 11.6531 8.10107 11.624 8.24907 11.624C8.39706 11.624 8.5436 11.6531 8.68033 11.7098C8.81706 11.7664 8.94129 11.8494 9.04594 11.9541L10.5 13.4062L14.9541 8.95125C15.0587 8.84684 15.183 8.76408 15.3196 8.7077C15.4563 8.65133 15.6027 8.62245 15.7506 8.62271C15.8984 8.62297 16.0448 8.65237 16.1812 8.70922C16.3177 8.76608 16.4416 8.84928 16.5459 8.95406ZM22.125 12C22.125 14.0025 21.5312 15.9601 20.4186 17.6251C19.3061 19.2902 17.7248 20.5879 15.8747 21.3543C14.0246 22.1206 11.9888 22.3211 10.0247 21.9305C8.06066 21.5398 6.25656 20.5755 4.84055 19.1595C3.42454 17.7435 2.46023 15.9393 2.06955 13.9753C1.67888 12.0112 1.87939 9.97543 2.64572 8.12533C3.41206 6.27523 4.70981 4.69392 6.37486 3.58137C8.0399 2.46882 9.99747 1.875 12 1.875C14.6844 1.87798 17.258 2.94567 19.1562 4.84383C21.0543 6.74199 22.122 9.3156 22.125 12ZM19.875 12C19.875 10.4425 19.4131 8.91992 18.5478 7.62488C17.6825 6.32985 16.4526 5.32049 15.0136 4.72445C13.5747 4.12841 11.9913 3.97246 10.4637 4.27632C8.93607 4.58017 7.53288 5.3302 6.43154 6.43153C5.3302 7.53287 4.58018 8.93606 4.27632 10.4637C3.97246 11.9913 4.12841 13.5747 4.72445 15.0136C5.32049 16.4526 6.32985 17.6825 7.62489 18.5478C8.91993 19.4131 10.4425 19.875 12 19.875C14.0879 19.8728 16.0896 19.0424 17.566 17.566C19.0424 16.0896 19.8728 14.0879 19.875 12Z" fill="#0EA674"/>
  </svg>
`;

const ShieldCheckIcon = ({ width = 16, height = 16, style = {} }) => html`
  <svg width=${width} height=${height} viewBox="0 0 16 16" fill="none" style=${style} xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_999_1410)">
      <path d="M13 2.25H3C2.66848 2.25 2.35054 2.3817 2.11612 2.61612C1.8817 2.85054 1.75 3.16848 1.75 3.5V7C1.75 10.3931 3.395 12.4513 4.775 13.5806C6.25688 14.7925 7.74 15.2056 7.8025 15.2238C7.93184 15.2587 8.06816 15.2587 8.1975 15.2238C8.26 15.2063 9.74313 14.7925 11.225 13.5806C12.605 12.4513 14.25 10.3931 14.25 7V3.5C14.25 3.16848 14.1183 2.85054 13.8839 2.61612C13.6495 2.3817 13.3315 2.25 13 2.25ZM12.75 7C12.75 9.23187 11.9319 11.0431 10.3181 12.3844C9.62641 12.9552 8.84248 13.4039 8 13.7113C7.15745 13.404 6.37349 12.9553 5.68188 12.3844C4.06813 11.0431 3.25 9.23187 3.25 7V3.75H12.75V7ZM4.96938 9.03063C4.89961 8.96086 4.84427 8.87804 4.80651 8.78689C4.76876 8.69573 4.74932 8.59804 4.74932 8.49938C4.74932 8.40071 4.76876 8.30302 4.80651 8.21186C4.84427 8.12071 4.89961 8.03789 4.96938 7.96812C5.03914 7.89836 5.12196 7.84302 5.21311 7.80526C5.30427 7.76751 5.40196 7.74807 5.50063 7.74807C5.59929 7.74807 5.69698 7.76751 5.78814 7.80526C5.87929 7.84302 5.96211 7.89836 6.03188 7.96812L7 8.9375L9.96937 5.9675C10.1103 5.8266 10.3014 5.74745 10.5006 5.74745C10.6999 5.74745 10.891 5.8266 11.0319 5.9675C11.1728 6.1084 11.2519 6.29949 11.2519 6.49875C11.2519 6.69801 11.1728 6.8891 11.0319 7.03L7.53188 10.53C7.4622 10.5999 7.3794 10.6554 7.28824 10.6933C7.19708 10.7311 7.09934 10.7506 7.00063 10.7506C6.90191 10.7506 6.80417 10.7311 6.71301 10.6933C6.62185 10.6554 6.53905 10.5999 6.46938 10.53L4.96938 9.03063Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_999_1410">
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
`;

// Minimum measurements the workshop can produce
const MIN_WIDTH_MM = 650;
const MIN_HEIGHT_MM = 200;

// A window name travels onto the order and the workshop's paperwork, where a long
// one gets cut off anyway. Capped here so what the customer types is what everyone
// downstream sees. Enforced on every write, not only the inputs, because a name can
// also arrive by duplicating a window.
const MAX_NAME_LENGTH = 25;
const capName = (value) => (value || '').slice(0, MAX_NAME_LENGTH);

// App Component
const MeasureApp = () => {
  // Config variables
  const config = window.MeasureAppConfig || {};
  config.assets = config.assets || {};
  const getLocale = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('locale')) return urlParams.get('locale').toLowerCase();
    return (config.locale || '').toLowerCase();
  };
  const locale = getLocale().substring(0, 2);
  const isNO = ['no', 'nb'].includes(locale);
  const isDE = locale === 'de';

  const t = (noText, deText, enText) => {
    if (isNO) return noText;
    if (isDE) return deText;
    return enText;
  };

  // Deep-link: the PDP primary CTA links here with ?from=pdp — those visitors
  // skip the intro/start screen and land directly on tool selection, then go
  // straight into measuring their first window (see selectTool). The init and
  // polling effects below must not re-route them to start/overview.
  const fromPdp = new URLSearchParams(window.location.search).get('from') === 'pdp';

  // State
  const [step, setStep] = useState(fromPdp ? 'measureTool' : 'start'); // start, overview, mount, type, slidingDoorWaterboardQ, slidingDoorHandleQ, width, height, waterboardDistance, handleDistance, name, revealDepthQ, revealDepthRecommendation, fasteningType, screwingHinderedQ, screwingHinderedRecommendation, measureTool
  const [windows, setWindows] = useState([]);
  const [draftWindow, setDraftWindow] = useState(null);
  const [measuringTool, setMeasuringTool] = useState(localStorage.getItem('screenlux_measuring_tool') || '');
  
  const [isSaving, setIsSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const getCustomerId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('logged_in_customer_id') || urlParams.get('customer_id') || (config.customer && config.customer.id);
  };
  const customerId = getCustomerId();
  const hasAccount = !!(config.customer?.hasAccount || customerId);
  // Every market gates the overview behind an account: finishing a window sends
  // guests to the login first, so measurements always end up on their profile.
  const forceLogin = !hasAccount;

  const getApiUrl = (basePath = '/apps/measurements') => {
    if (customerId) {
      const separator = basePath.includes('?') ? '&' : '?';
      return `${basePath}${separator}customer_id=${customerId}&logged_in_customer_id=${customerId}`;
    }
    return basePath;
  };

  // Measurement Guarantee State
  const [showGuaranteeModal, setShowGuaranteeModal] = useState(false);

  // Persistent "need help?" affordance — always available, on every step, so
  // a visitor stuck mid-measurement never has to hunt for a way to reach us.
  // Rendered once outside the step switch (see the main return at the bottom
  // of the component) rather than per-step, so it doesn't need to be wired
  // into every render*Step function individually.
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Opens the sitewide HubSpot Conversations widget. HubSpot's script exposes
  // window.HubSpotConversations once its own async load finishes; if it
  // hasn't yet, queue the open via the documented hsConversationsOnReady
  // callback array instead of failing silently.
  const openHubSpotChat = () => {
    setShowHelpModal(false);
    try {
      if (window.HubSpotConversations && window.HubSpotConversations.widget) {
        window.HubSpotConversations.widget.open();
      } else {
        window.hsConversationsOnReady = window.hsConversationsOnReady || [];
        window.hsConversationsOnReady.push(function () {
          window.HubSpotConversations.widget.open();
        });
      }
    } catch (e) {
      console.error('Failed to open HubSpot chat', e);
    }
  };

  // Free laser request ("GRATIS Laser erhalten" card on the tool step).
  // Same backend as the Gratis Stoffmuster popup (request-sample proxy
  // action), marked requestType: 'laser' — office mails the device manually
  // via the Powerups "Fabric Samples" admin page.
  const [showLaserModal, setShowLaserModal] = useState(false);
  const [laserForm, setLaserForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', addressLine1: '', addressLine2: '',
    zip: '', city: '', country: isDE ? 'Deutschland' : 'Norge'
  });
  const [laserStatus, setLaserStatus] = useState('idle'); // idle | missing | saving | done | error

  const submitLaserRequest = async () => {
    const f = laserForm;
    const required = [f.firstName, f.lastName, f.email, f.phone, f.addressLine1, f.zip, f.city, f.country];
    if (required.some(v => !v.trim())) {
      setLaserStatus('missing');
      return;
    }
    setLaserStatus('saving');
    let sid = '';
    try {
      sid = sessionStorage.getItem('slx_sid') || ('s' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8));
      sessionStorage.setItem('slx_sid', sid);
    } catch (e) {}
    try {
      const res = await fetch('/apps/measurements?action=request-sample', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestType: 'laser',
          // Discrete parts so HubSpot gets a clean firstname/lastname rather
          // than a split of one blob. `name` is still sent because the
          // metaobject stores a single name and the shipping label prints it.
          firstName: f.firstName.trim(),
          lastName: f.lastName.trim(),
          name: (f.firstName.trim() + ' ' + f.lastName.trim()).trim(),
          email: f.email.trim(),
          phone: f.phone.trim(),
          addressLine1: f.addressLine1.trim(),
          addressLine2: f.addressLine2.trim(),
          zip: f.zip.trim(),
          city: f.city.trim(),
          country: f.country.trim(),
          pageUrl: window.location.href,
          utm: attribution(),
          attr: attrTouches(),
          market: isDE ? 'de' : 'no',
          sessionId: sid
        })
      });
      if (!res.ok) throw new Error('request-sample (laser) failed');
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'laser_requested', { market: isDE ? 'de' : 'no' });
        }
      } catch (e) {}
      setLaserStatus('done');
      setTimeout(() => {
        setShowLaserModal(false);
        setLaserStatus('idle');
      }, 3000);
    } catch (e) {
      console.error('Free laser request failed', e);
      setLaserStatus('error');
    }
  };

  const saveMeasuringTool = async (tool) => {
    setMeasuringTool(tool);
    localStorage.setItem('screenlux_measuring_tool', tool);
    if (hasAccount && windows.length > 0) {
      setIsSaving(true);
      try {
        await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ windows, measuringTool: tool, market: isDE ? 'de' : 'no' })
        });
      } catch (e) {
        console.error('Failed to save measuring tool to profile', e);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleStart = () => {
    if (!measuringTool) {
      setStep('measureTool');
    } else {
      setStep('overview');
    }
  };

  // Initialize and sync windows
  useEffect(() => {
    const initWindows = async () => {
      const saved = localStorage.getItem('screenlux_measurements');
      let localWindows = [];
      if (saved && saved !== 'undefined') {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            localWindows = parsed.filter(w => w && typeof w === 'object' && w.id);
          }
        } catch (e) {
          console.error('Failed to parse saved measurements', e);
        }
      }

      if (hasAccount) {
        setIsFetching(true);
        try {
          // Fetch from app proxy
          const res = await fetch(getApiUrl());
          if (res.ok) {
            const data = await res.json();
            const remoteWindows = (data && Array.isArray(data.windows)) 
              ? data.windows.filter(w => w && typeof w === 'object' && w.id)
              : [];
            const remoteMeasuringTool = (data && data.measuringTool) || '';
            
            if (remoteMeasuringTool) {
              setMeasuringTool(remoteMeasuringTool);
              localStorage.setItem('screenlux_measuring_tool', remoteMeasuringTool);
            }
            
            // Map remote windows to have synced: true
            const remoteMapped = remoteWindows.map(rw => ({ ...rw, synced: true }));
            
            // Merge local and remote
            const merged = [...remoteMapped];
            let needsSave = false;
            
            localWindows.forEach(lw => {
              const existingIndex = merged.findIndex(mw => mw.id === lw.id);
              if (existingIndex > -1) {
                // If it already exists on remote, keep local version but mark as synced
                merged[existingIndex] = { ...merged[existingIndex], ...lw, synced: true };
              } else {
                // If it does not exist on remote:
                if (lw.synced) {
                  // This window was previously synced, so its absence in remote means it was
                  // deleted from the Shopify backend. Do NOT add it back.
                } else {
                  // This is a new local/anonymous window that hasn't been synced yet.
                  merged.push({ ...lw, synced: true });
                  needsSave = true;
                }
              }
            });

            const finalMerged = merged.filter(w => w && w.id);

            setWindows(finalMerged);
            if (fromPdp) {
              // PDP deep-link visitors always start at tool selection.
            } else if (hasAccount) {
              if (remoteMeasuringTool || localStorage.getItem('screenlux_measuring_tool')) {
                setStep('overview');
              } else {
                setStep('measureTool');
              }
            } else if (finalMerged.length > 0) {
              if (remoteMeasuringTool || localStorage.getItem('screenlux_measuring_tool')) {
                setStep('overview');
              } else {
                setStep('measureTool');
              }
            }
            
            if (needsSave) {
              await saveToRemote(finalMerged);
            } else {
              localStorage.setItem('screenlux_measurements', JSON.stringify(finalMerged));
            }
          }
        } catch (e) {
          console.error("Failed to fetch windows from customer profile", e);
          const finalLocal = localWindows.filter(w => w && w.id);
          setWindows(finalLocal);
          if (fromPdp) {
            // PDP deep-link visitors always start at tool selection.
          } else if (hasAccount) {
            if (localStorage.getItem('screenlux_measuring_tool')) {
              setStep('overview');
            } else {
              setStep('measureTool');
            }
          } else if (finalLocal.length > 0) {
            if (localStorage.getItem('screenlux_measuring_tool')) {
              setStep('overview');
            } else {
              setStep('measureTool');
            }
          }
        } finally {
          setIsFetching(false);
        }
      } else {
        const finalLocal = localWindows.filter(w => w && w.id);
        setWindows(finalLocal);
        if (finalLocal.length > 0 && !fromPdp) {
          if (localStorage.getItem('screenlux_measuring_tool')) {
            setStep('overview');
          } else {
            setStep('measureTool');
          }
        }
      }
    };

    initWindows();
  }, []);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Prevent numeric input changes on scroll/wheel
  useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('wheel', handleWheel);
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Polling for updates if on desktop and logged in / customerId is set
  useEffect(() => {
    if (!hasAccount) return;
    
    // Only poll on desktop (window.innerWidth > 749)
    const isDesktop = window.innerWidth > 749;
    if (!isDesktop) return;

    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(getApiUrl());
        if (res.ok) {
          const data = await res.json();
          const remoteWindows = (data && Array.isArray(data.windows)) 
            ? data.windows.filter(w => w && typeof w === 'object' && w.id)
            : [];
          
          setWindows(prevWindows => {
            const currentSerialized = JSON.stringify(prevWindows.map(w => ({ id: w.id, name: w.name, widthTop: w.widthTop, heightLeft: w.heightLeft })));
            const remoteSerialized = JSON.stringify(remoteWindows.map(w => ({ id: w.id, name: w.name, widthTop: w.widthTop, heightLeft: w.heightLeft })));
            
            if (currentSerialized !== remoteSerialized) {
              console.log("Polled and found updated windows from database:", remoteWindows);
              const remoteMapped = remoteWindows.map(rw => ({ ...rw, synced: true }));
              localStorage.setItem('screenlux_measurements', JSON.stringify(remoteMapped));
              
              if ((step === 'start' || step === 'measureTool') && !fromPdp) {
                setStep('overview');
              }
              return remoteMapped;
            }
            return prevWindows;
          });
        }
      } catch (err) {
        console.error("Failed to poll for database updates:", err);
      }
    }, 4000);

    return () => clearInterval(pollInterval);
  }, [hasAccount, step]);

  const saveToRemote = async (windowsToSave) => {
    setIsSaving(true);
    try {
      const res = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ windows: windowsToSave, measuringTool, market: isDE ? 'de' : 'no' })
      });
      // Mark all saved windows as synced: true in local storage and state
      const syncedWindows = windowsToSave.map(w => ({ ...w, synced: true }));
      localStorage.setItem('screenlux_measurements', JSON.stringify(syncedWindows));
      setWindows(syncedWindows);
      // Fire mid-funnel "measurements submitted" conversion (once per session).
      if (res.ok) trackMeasurementsSubmitted(windowsToSave);
    } catch(e) {
      console.error('Failed to save to profile', e);
    } finally {
      setIsSaving(false);
    }
  };

  const saveToStorage = async (newWindows) => {
    const cleanWindows = Array.isArray(newWindows) ? newWindows.filter(w => w && w.id) : [];
    setWindows(cleanWindows);
    localStorage.setItem('screenlux_measurements', JSON.stringify(cleanWindows));
    
    if (hasAccount) {
      await saveToRemote(cleanWindows);
    } else if (!forceLogin) {
      // Guests never reach saveToRemote, which is where the mid-funnel
      // conversion normally fires. Fire it here so removing the login gate
      // doesn't silently drop GA4 / Google Ads / Meta signal for guests.
      // Internally guarded: once per session, and only with a real w+h.
      //
      // Skipped where the gate is still up: those guests are on their way to
      // the login wall, and saveToRemote fires this the moment they come back
      // with an account. Counting them here would book a conversion for people
      // the gate then loses.
      trackMeasurementsSubmitted(cleanWindows);
    }
  };

  const getWindowOverhangObstruction = (w) => {
    if (!w) return null;
    const isWall = w.mountType === 'Wall mounted' || w.mountType === 'Utvendig';
    if (!isWall) return null;
    if (w.hasRoofOverhang !== 'Yes') return null;

    const isOutward = w.windowOpeningOut === 'Yes' || w.doorOpening === 'out' || w.openingDirection === 'out';
    if (!isOutward) return null;

    const wTop = parseInt(w.widthTop) || 0;
    const wMid = parseInt(w.widthMiddle) || 0;
    const wBot = parseInt(w.widthBottom) || 0;
    const maxW = Math.max(wTop, wMid, wBot);

    const hLeft = parseInt(w.heightLeft) || 0;
    const hRight = parseInt(w.heightRight) || 0;
    const minH = Math.min(hLeft, hRight);

    if (!maxW || !minH) return null;

    // Keep in step with ScreenluxEngine.cassette (screenlux-engine.js), which
    // this module does not load.
    const cassetteSize = (maxW > 3000 || (minH + 115) > 2100) ? 'large' : 'slim';
    const totalNeed = cassetteSize === 'large' ? 165 : 115;
    const overhangDist = parseInt(w.roofOverhangDistance) || 0;
    
    if (overhangDist < totalNeed) {
      const buffer = 5;
      const spaceAbove = Math.max(0, overhangDist - buffer);
      const intrusion = totalNeed - spaceAbove;
      if (intrusion > 0) {
        return intrusion;
      }
    }
    return null;
  };

  // Measurement Guarantee Helper Computations and Actions

  const renderGuaranteeModal = () => {
    const modalTitle = t('100% Målegaranti detaljer', '100% Passgarantie Details', '100% Fit Guarantee Details');
    
    const firstTerm = isDE
      ? 'Solltest du dich vermessen haben, während die 100% Passgarantie aktiv ist, passen wir deine Screens kostenlos an oder ersetzen sie.'
      : (isNO
        ? 'Dersom du måler feil når 100% målegarantien er aktiv, vil vi tilpasse eller erstatte dine screens uten ekstra kostnad.'
        : 'If you measure incorrectly while the 100% Fit Guarantee is active, we will replace or repair the screens free of charge.');

    const otherTerms = isDE ? [
      'Die 100% Passgarantie gilt für maximal 5 Zip-Screens pro Kunde. Jeder Zip-Screen kann nur einmalig ersetzt bzw. angepasst werden.',
      'Abweichungen von mehr als 5 cm (50 mm) sind von der 100% Passgarantie ausgeschlossen, da dies als grober Messfehler und nicht als normale Messtoleranz eingestuft wird.',
      'Wir behalten uns das Recht vor, dich telefonisch zu kontaktieren, wenn wir weitere Details zu deinem Projekt benötigen.',
      'Sollten wir eine Überprüfung für notwendig halten, setzen wir die Produktion zu deiner Sicherheit aus, bis wir die Maße mit dir abgestimmt haben. Wir kontaktieren dich sowohl per Telefon als auch per E-Mail, um dich bestmöglich zu erreichen.'
    ] : (isNO ? [
      '100% Målegarantien gjelder for maksimalt 5 Zip-screens per kunde. Hver Zip-screen kan kun erstattes eller tilpasses én gang.',
      'Dersom målene avviker med mer enn 5 cm (50 mm), faller dette utenfor 100% målegarantien, da dette anses som en grov feilmåling og ikke en vanlig marginfeil.',
      'Vi forbeholder oss retten til å ringe deg dersom vi trenger mer informasjon om ditt prosjekt.',
      'Dersom vi anser en sjekk som nødvendig, vil vi for din sikkerhet sette produksjonen på vent til vi har bekreftet målene med deg. Vi vil kontakte deg via både telefon og e-post.'
    ] : [
      'The guarantee applies to a maximum of 5 Zip-screens per customer. Each Zip-screen can only be replaced or repaired once.',
      'If the measurements deviate by more than 5 cm (50 mm), it is outside the scope of the guarantee, as this is considered a significant mistake rather than a measuring tolerance.',
      'We are allowed to call you if we need more information about your project.',
      'If we believe a double-check is necessary, we will put production on hold for your safety until we have confirmed the measurements with you. We will contact you via both phone and email.'
    ]);

    const closeText = t('Lukk', 'Schließen', 'Close');

    return html`
      <div class="guarantee-modal-overlay">
        <div class="guarantee-modal-card" style="max-width: 500px; padding: 24px; position: relative;">
          <button class="guarantee-close-btn" style="position: absolute; top: 16px; right: 16px;" onClick=${() => setShowGuaranteeModal(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div style="margin-bottom: 20px; display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 18px; color: #137333;">
            <${ShieldCheckIcon} width="24" height="24" style=${{ color: '#137333', flexShrink: 0 }} />
            <span>${modalTitle}</span>
          </div>
          <div style="font-size: 14px; line-height: 1.6; color: rgba(var(--color-foreground), 0.8); max-height: 60vh; overflow-y: auto; text-align: left; margin-bottom: 20px;">
            <p style=${{ fontSize: '15px', fontWeight: '600', color: 'rgba(var(--color-foreground), 0.9)', marginBottom: '16px' }}>${firstTerm}</p>
            <ul style=${{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px', listStyleType: 'disc' }}>
              ${otherTerms.map(t => html`<li style=${{ marginBottom: 0 }}>${t}</li>`)}
            </ul>
          </div>
          <button class="button--brand full-width" style="margin-top: 12px;" onClick=${() => setShowGuaranteeModal(false)}>
            ${closeText}
          </button>
        </div>
      </div>
    `;
  };

  // Address form for the free laser — mirrors the Gratis Stoffmuster popup
  // (same fields, same backend), minus the fabric color picker.
  const renderFreeLaserModal = () => {
    const field = (key, label, type = 'text', autocomplete = '') => html`
      <input class="sl-input" type=${type} autocomplete=${autocomplete} placeholder=${label}
             value=${laserForm[key]}
             onInput=${e => { const v = e.target.value; setLaserForm(prev => ({ ...prev, [key]: v })); }} />
    `;
    const busy = laserStatus === 'saving';

    return html`
      <div class="guarantee-modal-overlay" onClick=${e => { if (e.target === e.currentTarget && !busy) setShowLaserModal(false); }}>
        <div class="guarantee-modal-card" style="max-width: 460px; padding: 24px; position: relative; text-align: left;">
          <button class="guarantee-close-btn" style="position: absolute; top: 16px; right: 16px;" onClick=${() => setShowLaserModal(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          ${laserStatus === 'done' ? html`
            <div style="text-align: center; padding: 24px 0;">
              <p style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">${t('Takk! 🙏', 'Danke! 🙏', 'Thank you! 🙏')}</p>
              <p style="color: rgba(var(--color-foreground), 0.7); margin: 0;">
                ${t(
                  'Bestillingen din er mottatt – vi sender lasermåleren til deg snart.',
                  'Deine Anfrage ist bei uns eingegangen – wir schicken dir das Lasermessgerät in Kürze zu.',
                  'Your request has been received – we will send you the laser measure shortly.'
                )}
              </p>
            </div>
          ` : html`
            <div style="margin-bottom: 4px; font-weight: 700; font-size: 18px;">
              ${t('GRATIS lasermåler', 'GRATIS Lasermessgerät', 'FREE laser measure')}
            </div>
            <p style="color: rgba(var(--color-foreground), 0.7); font-size: 14px; margin: 0 0 16px 0;">
              ${t(
                'Fyll inn adressen din – vi sender deg en lasermåler helt gratis.',
                'Gib deine Adresse ein – wir schicken dir ein Lasermessgerät völlig kostenlos zu.',
                'Enter your address – we will send you a laser measure completely free of charge.'
              )}
            </p>
            <div style="display: flex; flex-direction: column; gap: 10px;">
              <div style="display: flex; gap: 10px;">
                <div style="flex: 1;">${field('firstName', t('Fornavn', 'Vorname', 'First name'), 'text', 'given-name')}</div>
                <div style="flex: 1;">${field('lastName', t('Etternavn', 'Nachname', 'Last name'), 'text', 'family-name')}</div>
              </div>
              ${field('email', t('E-post', 'E-Mail', 'Email'), 'email', 'email')}
              ${field('phone', t('Telefon', 'Telefon', 'Phone'), 'tel', 'tel')}
              ${field('addressLine1', t('Gate og husnummer', 'Straße & Hausnummer', 'Street & number'), 'text', 'address-line1')}
              ${field('addressLine2', t('Adressetillegg (valgfritt)', 'Adresszusatz (optional)', 'Address line 2 (optional)'), 'text', 'address-line2')}
              <div style="display: flex; gap: 10px;">
                <div style="flex: 1;">${field('zip', t('Postnummer', 'PLZ', 'ZIP'), 'text', 'postal-code')}</div>
                <div style="flex: 2;">${field('city', t('Sted', 'Stadt', 'City'), 'text', 'address-level2')}</div>
              </div>
              ${field('country', t('Land', 'Land', 'Country'), 'text', 'country-name')}
            </div>
            ${laserStatus === 'missing' ? html`
              <p style="color: #dc2626; font-size: 13px; margin: 10px 0 0 0;">
                ${t('Fyll ut alle obligatoriske felt.', 'Bitte fülle alle Pflichtfelder aus.', 'Please fill in all required fields.')}
              </p>
            ` : ''}
            ${laserStatus === 'error' ? html`
              <p style="color: #dc2626; font-size: 13px; margin: 10px 0 0 0;">
                ${t('Noe gikk galt. Prøv igjen.', 'Da ist etwas schiefgelaufen. Bitte versuche es erneut.', 'Something went wrong. Please try again.')}
              </p>
            ` : ''}
            <button class="button--brand full-width" style="margin-top: 16px;" disabled=${busy} onClick=${submitLaserRequest}>
              ${busy
                ? t('Sender …', 'Wird gesendet …', 'Sending …')
                : t('Bestill gratis lasermåler', 'GRATIS Laser anfordern', 'Request free laser')}
            </button>
          `}
        </div>
      </div>
    `;
  };

  // Same avatar + "online" dot as the top-bar (sections/top-bar.liquid,
  // .top-bar__profile-img/-dot) — a real face reads as "a person is here"
  // far better than an icon or wording alone. Static for now (not tied to
  // real business hours yet); shared by the trigger link and the modal so
  // the size is the only thing that differs between the two call sites.
  const renderSupportAvatar = (size) => html`
    <span style="position: relative; display: inline-block; width: ${size}px; height: ${size}px; flex-shrink: 0;">
      <img src=${config.supportPhoto} alt="" width=${size} height=${size}
           style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block;" />
      <span style="position: absolute; bottom: -1px; right: -1px; width: ${Math.round(size * 0.36)}px; height: ${Math.round(size * 0.36)}px; background: #27d923; border-radius: 50%; border: 2px solid #ffffff;"></span>
    </span>
  `;

  // "Need help?" modal — same shell as the guarantee/laser modals. Offers a
  // direct call (same number as the top-bar) and the sitewide HubSpot chat.
  const renderHelpModal = () => {
    const telHref = 'tel:' + (config.supportPhone || '').replace(/\s/g, '');
    return html`
      <div class="guarantee-modal-overlay" onClick=${e => { if (e.target === e.currentTarget) setShowHelpModal(false); }}>
        <div class="guarantee-modal-card" style="max-width: 420px; padding: 24px; position: relative; text-align: left;">
          <button class="guarantee-close-btn" style="position: absolute; top: 16px; right: 16px;" onClick=${() => setShowHelpModal(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 4px;">
            ${renderSupportAvatar(44)}
            <div>
              <div style="font-weight: 700; font-size: 18px;">
                ${t('Trenger du hjelp?', 'Brauchst du Hilfe?', 'Need help?')}
              </div>
              <div style="color: #137333; font-weight: 600; font-size: 12px; margin-top: 2px;">
                ${t('Vi er online', 'Wir sind online', "We're online")}
              </div>
            </div>
          </div>
          <p style="color: rgba(var(--color-foreground), 0.7); margin: 0 0 20px 0;">
            ${t(
              'Vi hjelper deg gjerne videre – ring oss eller start en chat. Vi svarer vanligvis i løpet av noen minutter.',
              'Wir helfen dir gerne weiter – ruf uns an oder starte einen Chat. Wir antworten meist innerhalb weniger Minuten.',
              'We are happy to help — call us or start a chat. We usually reply within a few minutes.'
            )}
          </p>
          <div style="display: flex; flex-direction: column; gap: 10px;">
            <a class="button--brand-secondary full-width"
               style="text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 6px;"
               href=${telHref}
               onClick=${() => setShowHelpModal(false)}>
              ${t('Ring oss', 'Anrufen', 'Call us')}
              ${config.supportPhone ? html`<span style="font-weight: 400;">${config.supportPhone}</span>` : ''}
            </a>
            <button class="button--brand full-width" onClick=${openHubSpotChat}>
              ${t('Start chat', 'Chat starten', 'Start chat')}
            </button>
          </div>
        </div>
      </div>
    `;
  };

   // toolOverride: selectTool calls this right after saveMeasuringTool, when
   // the measuringTool state in this closure is still the previous value —
   // it passes the just-chosen tool explicitly. (typeof check so a stray
   // event object from an onClick can never count as a tool.)
   const startNewWindow = (toolOverride) => {
    // Funnel: user began measuring a window (once per session).
    trackFunnelEvent('measure_started');
    const activeTool = typeof toolOverride === 'string' ? toolOverride : measuringTool;
    if (!activeTool) {
      setStep('measureTool');
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const isAiBeta = urlParams.get('ai_beta') === 'true';

    setDraftWindow({
      id: Date.now().toString(),
      type: '',
      mountType: '',
      flowType: isAiBeta ? '' : 'manual',
      // Version 1 fields
      widthTop: '',
      widthMiddle: '',
      widthBottom: '',
      heightLeft: '',
      heightRight: '',
      // Version 2 fields
      width: '',
      height: '',
      waterboardDistance: '',
      handleDistance: '',
      hasWaterboard: '',
      hasHandle: '',
      // Version 3 fields
      windowOpeningOut: '',
      topToWaterboardHeight: '',
      doorOpening: '',
      hasRoofOverhang: '',
      roofOverhangDistance: '',
      // German fields
      fasteningType: '',
      revealDepth: '',
      screwingHindered: '',
      name: '',
      image: null
    });
    
    if (isNO || isDE) {
      if (isAiBeta) {
        setStep('flowSelection');
      } else {
        setStep('mount');
      }
    } else {
      setStep('type');
    }
  };

  const editWindow = (id) => {
    const win = Array.isArray(windows) ? windows.find(w => w && w.id === id) : null;
    if (win) {
      setDraftWindow({ ...win });
      if (isNO || isDE) {
        const urlParams = new URLSearchParams(window.location.search);
        const isAiBeta = urlParams.get('ai_beta') === 'true';
        if (isAiBeta && win.flowType === 'ai') {
          setStep('aiConfirm');
        } else {
          setStep('mount');
        }
      } else {
        setStep('type');
      }
    }
  };

  const removeWindow = (id) => {
    if (Array.isArray(windows)) {
      saveToStorage(windows.filter(w => w && w.id !== id));
    }
  };

  const duplicateWindow = (id) => {
    const win = Array.isArray(windows) ? windows.find(w => w && w.id === id) : null;
    if (win) {
      const copySuffix = isDE ? ' (Kopie)' : (isNO ? ' (Kopi)' : ' (Copy)');
      const currentWindows = Array.isArray(windows) ? windows.filter(w => w && w.id) : [];
      saveToStorage([...currentWindows, { ...win, id: Date.now().toString(), name: win.name ? capName(win.name + copySuffix) : '', measurementGuarantee: getWindowOverhangObstruction(win) ? false : true }]);
    }
  };

  const updateWindowName = (id, newName) => {
    if (Array.isArray(windows)) {
      const updated = windows.map(w => {
        if (w && w.id === id) {
          return { ...w, name: capName(newName) };
        }
        return w;
      });
      saveToStorage(updated);
    }
  };

  const compressImage = (file, onSuccess, onError) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Src = event.target.result;
      const img = new Image();
      img.onload = () => {
        const maxDim = 1200;
        let width = img.width;
        let height = img.height;
        
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.85);
        onSuccess(compressedBase64, base64Src);
      };
      img.onerror = () => {
        if (onError) onError(new Error("Failed to load image"));
      };
      img.src = base64Src;
    };
    reader.onerror = () => {
      if (onError) onError(new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      compressImage(
        file,
        (compressed) => {
          setDraftWindow(prev => ({ ...prev, image: compressed }));
        },
        (err) => {
          console.error("Image compression failed:", err);
        }
      );
      e.target.value = '';
    }
  };

  // Step transitions helpers
  const nextStep = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAiBeta = urlParams.get('ai_beta') === 'true';

    // If AI flow is active, navigate strictly using getStepsList()
    if (isAiBeta && draftWindow.flowType === 'ai') {
      const list = getStepsList();
      const index = list.indexOf(step);
      if (index !== -1 && index < list.length - 1) {
        setStep(list[index + 1]);
      }
      return;
    }

    if (isDE) {
      switch(step) {
        case 'mount':
          if (draftWindow.mountType === 'Recess mounted') {
            setStep('fasteningType');
          } else if (draftWindow.mountType === 'Wall mounted') {
            setStep('width');
          } else { // 'Unknown'
            setStep('revealDepthQ');
          }
          break;
        case 'revealDepthQ':
          const depth = parseInt(draftWindow.revealDepth, 10) || 0;
          const recommendRecess = depth >= 40;
          setDraftWindow(prev => ({ 
            ...prev, 
            mountType: recommendRecess ? 'Recess mounted' : 'Wall mounted' 
          }));
          setStep('revealDepthRecommendation');
          break;
        case 'revealDepthRecommendation':
          if (draftWindow.mountType === 'Recess mounted') {
            setStep('fasteningType');
          } else {
            setStep('width');
          }
          break;
        case 'fasteningType':
          if (draftWindow.fasteningType === 'Unknown') {
            setStep('screwingHinderedQ');
          } else {
            setStep('width');
          }
          break;
        case 'screwingHinderedQ':
          const noObstacles = draftWindow.screwingHindered === 'No';
          setDraftWindow(prev => ({ 
            ...prev, 
            fasteningType: noObstacles ? 'In the window frame' : 'Sideways in the wall' 
          }));
          setStep('screwingHinderedRecommendation');
          break;
        case 'screwingHinderedRecommendation':
          setStep('width');
          break;
        case 'width':
          setStep('height');
          break;
        case 'height':
          setStep('name');
          break;
      }
      return;
    }

    if (!isNO) {
      switch(step) {
        case 'type': setStep('width'); break;
        case 'width': setStep('height'); break;
        case 'height': setStep('mount'); break;
        case 'mount': setStep('name'); break;
      }
      return;
    }

    // Version 3 NO Step Transitions using dynamic steps list
    const list = getStepsList();
    const index = list.indexOf(step);
    if (index !== -1 && index < list.length - 1) {
      setStep(list[index + 1]);
    }
  };

  const prevStep = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAiBeta = urlParams.get('ai_beta') === 'true';

    // If AI flow is active, navigate strictly using getStepsList()
    if (isAiBeta && draftWindow.flowType === 'ai') {
      const list = getStepsList();
      const index = list.indexOf(step);
      if (index > 0) {
        setStep(list[index - 1]);
      } else {
        setStep('overview');
      }
      return;
    }

    if (isDE) {
      switch(step) {
        case 'mount':
          if (isAiBeta) {
            setStep('flowSelection');
          } else {
            setStep('overview');
          }
          break;
        case 'revealDepthQ':
          setStep('mount');
          break;
        case 'revealDepthRecommendation':
          setStep('revealDepthQ');
          break;
        case 'fasteningType':
          if (draftWindow.revealDepth) {
            setStep('revealDepthRecommendation');
          } else {
            setStep('mount');
          }
          break;
        case 'screwingHinderedQ':
          setStep('fasteningType');
          break;
        case 'screwingHinderedRecommendation':
          setStep('screwingHinderedQ');
          break;
        case 'width':
          if (draftWindow.mountType === 'Recess mounted') {
            if (draftWindow.screwingHindered) {
              setStep('screwingHinderedRecommendation');
            } else {
              setStep('fasteningType');
            }
          } else {
            if (draftWindow.revealDepth) {
              setStep('revealDepthRecommendation');
            } else {
              setStep('mount');
            }
          }
          break;
        case 'height':
          setStep('width');
          break;
        case 'name':
          setStep('height');
          break;
      }
      return;
    }

    if (!isNO) {
      switch(step) {
        case 'type': setStep('overview'); break;
        case 'width': setStep('type'); break;
        case 'height': setStep('width'); break;
        case 'mount': setStep('height'); break;
        case 'name': setStep('mount'); break;
      }
      return;
    }

    // Version 3 NO Step Transitions Backwards using dynamic steps list
    const list = getStepsList();
    const index = list.indexOf(step);
    if (index > 0) {
      setStep(list[index - 1]);
    } else {
      setStep('overview');
    }
  };

  // Get dynamic steps list for progress calculation
  const getStepsList = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAiBeta = urlParams.get('ai_beta') === 'true';

    // 1. If AI scan flow is active, we use the detailed V3 (Norwegian style) measurement steps for both NO and DE
    if (isAiBeta && draftWindow.flowType === 'ai') {
      const list = ['flowSelection', 'aiUpload', 'aiConfirm'];
      if (draftWindow.mountType === 'Recess mounted') {
        list.push('inside-frame_width', 'inside-frame_height', 'name');
        return list;
      }
      
      if (draftWindow.mountType === 'Wall mounted') {
        if (draftWindow.type === 'Window without waterboard on top') {
          list.push('on-wall_without-waterboard-on-top_width', 'on-wall_without-waterboard-on-top_height');
        } else if (draftWindow.type === 'Window with waterboard on top') {
          const openingOutVal = draftWindow.windowOpeningOut || (draftWindow.doorOpening === 'out' ? 'Yes' : (draftWindow.doorOpening === 'in' ? 'No' : ''));
          if (openingOutVal === 'Yes') {
            list.push('on-wall_with-waterboard-on-top_heigth-from-top-window-to-waterboard');
            list.push('on-wall_with-waterboard-on-top_width');
            
            const wTop = parseInt(draftWindow.widthTop) || 0;
            const wMid = parseInt(draftWindow.widthMiddle) || 0;
            const wBot = parseInt(draftWindow.widthBottom) || 0;
            const maxW = Math.max(wTop, wMid, wBot);
            const finalWidth = maxW + 76;
            const cassetteNeed = finalWidth > 3000 ? 120 : 85;
            const clearance = parseInt(draftWindow.topToWaterboardHeight) || 0;
            
            list.push('on-wall_with-waterboard-on-top_height-to-top-window');
            if (clearance < cassetteNeed) {
              list.push('on-wall_waterboard-out-from-wall');
            }
          } else {
            list.push('on-wall_with-waterboard-on-top_width');
            list.push('on-wall_with-waterboard-on-top_height-to-waterboard');
          }
        } else if (draftWindow.type === 'Sliding door') {
          list.push('on-wall_waterboard-question', 'on-wall_sticking-out-handle-question');
          if (draftWindow.hasWaterboard === 'Yes') {
            list.push('on-wall_sliding-door_with-waterboard-width', 'on-wall_sliding-door_with-waterboard-heigth');
          } else {
            list.push('on-wall_sliding-door_without-waterboard-width', 'on-wall_sliding-door_without-waterboard-heigth');
          }
          if (draftWindow.hasHandle === 'Yes') {
            list.push('on-wall_sticking-out-handle-measure');
          }
        } else if (draftWindow.type === 'Terrace door') {
          list.push('on-wall_waterboard-question', 'on-wall_sticking-out-handle-question', 'on-wall_terrace-door_open-direction');
          if (draftWindow.hasWaterboard === 'No') {
            list.push('on-wall_sliding-door_without-waterboard-width-1', 'on-wall_sliding-door_without-waterboard-heigth-1');
          } else if (draftWindow.hasWaterboard === 'Yes') {
            const doorOpeningVal = draftWindow.doorOpening || (draftWindow.windowOpeningOut === 'Yes' ? 'out' : (draftWindow.windowOpeningOut === 'No' ? 'in' : 'in'));
            if (doorOpeningVal === 'in') {
              list.push('on-wall_sliding-door_skyvedor-with-vanbrett-width', 'on-wall_sliding-door_skyvedor-with-vanbrett-height-to-waterboard');
            } else if (doorOpeningVal === 'out') {
              list.push('on-wall_sliding-door_waterboard-measure');
              list.push('on-wall_sliding-door_skyvedor-with-vanbrett-width');
              
              const wTop = parseInt(draftWindow.widthTop) || 0;
              const wMid = parseInt(draftWindow.widthMiddle) || 0;
              const wBot = parseInt(draftWindow.widthBottom) || 0;
              const maxW = Math.max(wTop, wMid, wBot);
              const finalWidth = maxW + 76;
              const cassetteNeed = finalWidth > 3000 ? 120 : 85;
              const clearance = parseInt(draftWindow.topToWaterboardHeight) || 0;
              
              list.push('on-wall_sliding-door_skyvedor-with-vanbrett-height-to-top-door');
              if (clearance < cassetteNeed) {
                list.push('on-wall_waterboard-out-from-wall');
              }
            }
          }
          if (draftWindow.hasHandle === 'Yes') {
            list.push('on-wall_sticking-out-handle-measure');
          }
        }
        const opOut = draftWindow.windowOpeningOut || (draftWindow.doorOpening === 'out' ? 'Yes' : (draftWindow.doorOpening === 'in' ? 'No' : ''));
        const drOp = draftWindow.doorOpening || (draftWindow.windowOpeningOut === 'Yes' ? 'out' : (draftWindow.windowOpeningOut === 'No' ? 'in' : 'in'));
        const isMountingUnderWater = 
          (draftWindow.type === 'Window with waterboard on top' && opOut !== 'Yes') ||
          (draftWindow.type === 'Sliding door' && draftWindow.hasWaterboard === 'Yes') ||
          (draftWindow.type === 'Terrace door' && draftWindow.hasWaterboard === 'Yes' && drOp !== 'out');

        if (!isMountingUnderWater) {
          list.push('on-wall_roof-overhang-question');
          if (draftWindow.hasRoofOverhang === 'Yes') {
            list.push('on-wall_roof-overhang-measure');
          }
        }
        list.push('name');
      }
      return list;
    }

    // 2. German manual flow steps list (always has flowSelection first if beta is enabled)
    if (isDE) {
      const list = isAiBeta ? ['flowSelection', 'mount'] : ['mount'];
      if (draftWindow.mountType === 'Unknown' || draftWindow.revealDepth) {
        list.push('revealDepthQ');
        list.push('revealDepthRecommendation');
      }
      if (draftWindow.mountType === 'Recess mounted') {
        list.push('fasteningType');
        if (draftWindow.fasteningType === 'Unknown' || draftWindow.screwingHindered) {
          list.push('screwingHinderedQ');
          list.push('screwingHinderedRecommendation');
        }
      }
      list.push('width');
      list.push('height');
      if (draftWindow.mountType === 'Wall mounted') {
        list.push('on-wall_roof-overhang-question');
        if (draftWindow.hasRoofOverhang === 'Yes') {
          list.push('on-wall_roof-overhang-measure');
        }
      }
      list.push('name');
      return list;
    }

    // 3. Standard non-Norwegian market steps list
    if (!isNO) {
      return ['type', 'width', 'height', 'mount', 'name'];
    }

    // 4. Norwegian manual flow steps list
    const list = (isNO && isAiBeta) ? ['flowSelection', 'mount'] : ['mount'];
    if (draftWindow.mountType === 'Recess mounted') {
      list.push('inside-frame_width', 'inside-frame_height', 'name');
      return list;
    }
    
    if (draftWindow.mountType === 'Wall mounted') {
      list.push('type');
      if (draftWindow.type === 'Window without waterboard on top') {
        list.push('on-wall_without-waterboard-on-top_width', 'on-wall_without-waterboard-on-top_height');
      } else if (draftWindow.type === 'Window with waterboard on top') {
        list.push('on-wall_with-waterboard-on-top_window-opening-out');
        if (draftWindow.windowOpeningOut === 'Yes') {
          list.push('on-wall_with-waterboard-on-top_heigth-from-top-window-to-waterboard');
          list.push('on-wall_with-waterboard-on-top_width');
          
          // Determine cassette need
          const wTop = parseInt(draftWindow.widthTop) || 0;
          const wMid = parseInt(draftWindow.widthMiddle) || 0;
          const wBot = parseInt(draftWindow.widthBottom) || 0;
          const maxW = Math.max(wTop, wMid, wBot);
          const finalWidth = maxW + 76;
          const cassetteNeed = finalWidth > 3000 ? 120 : 85;
          const clearance = parseInt(draftWindow.topToWaterboardHeight) || 0;
          
          list.push('on-wall_with-waterboard-on-top_height-to-top-window');
          if (clearance < cassetteNeed) {
            list.push('on-wall_waterboard-out-from-wall');
          }
        } else if (draftWindow.windowOpeningOut === 'No') {
          list.push('on-wall_with-waterboard-on-top_width');
          list.push('on-wall_with-waterboard-on-top_height-to-waterboard');
        }
      } else if (draftWindow.type === 'Sliding door') {
        list.push('on-wall_waterboard-question', 'on-wall_sticking-out-handle-question');
        if (draftWindow.hasWaterboard === 'Yes') {
          list.push('on-wall_sliding-door_with-waterboard-width', 'on-wall_sliding-door_with-waterboard-heigth');
        } else if (draftWindow.hasWaterboard === 'No') {
          list.push('on-wall_sliding-door_without-waterboard-width', 'on-wall_sliding-door_without-waterboard-heigth');
        }
        if (draftWindow.hasHandle === 'Yes') {
          list.push('on-wall_sticking-out-handle-measure');
        }
      } else if (draftWindow.type === 'Terrace door') {
        list.push('on-wall_waterboard-question', 'on-wall_sticking-out-handle-question', 'on-wall_terrace-door_open-direction');
        if (draftWindow.hasWaterboard === 'No') {
          list.push('on-wall_sliding-door_without-waterboard-width-1', 'on-wall_sliding-door_without-waterboard-heigth-1');
        } else if (draftWindow.hasWaterboard === 'Yes') {
          if (draftWindow.doorOpening === 'in') {
            list.push('on-wall_sliding-door_skyvedor-with-vanbrett-width', 'on-wall_sliding-door_skyvedor-with-vanbrett-height-to-waterboard');
          } else if (draftWindow.doorOpening === 'out') {
            list.push('on-wall_sliding-door_waterboard-measure');
            list.push('on-wall_sliding-door_skyvedor-with-vanbrett-width');
            
            const wTop = parseInt(draftWindow.widthTop) || 0;
            const wMid = parseInt(draftWindow.widthMiddle) || 0;
            const wBot = parseInt(draftWindow.widthBottom) || 0;
            const maxW = Math.max(wTop, wMid, wBot);
            const finalWidth = maxW + 76;
            const cassetteNeed = finalWidth > 3000 ? 120 : 85;
            const clearance = parseInt(draftWindow.topToWaterboardHeight) || 0;
            
            list.push('on-wall_sliding-door_skyvedor-with-vanbrett-height-to-top-door');
            if (clearance < cassetteNeed) {
              list.push('on-wall_waterboard-out-from-wall');
            }
          }
        }
        if (draftWindow.hasHandle === 'Yes') {
          list.push('on-wall_sticking-out-handle-measure');
        }
      }
      const opOut = draftWindow.windowOpeningOut || (draftWindow.doorOpening === 'out' ? 'Yes' : (draftWindow.doorOpening === 'in' ? 'No' : ''));
      const drOp = draftWindow.doorOpening || (draftWindow.windowOpeningOut === 'Yes' ? 'out' : (draftWindow.windowOpeningOut === 'No' ? 'in' : 'in'));
      const isMountingUnderWater = 
        (draftWindow.type === 'Window with waterboard on top' && opOut !== 'Yes') ||
        (draftWindow.type === 'Sliding door' && draftWindow.hasWaterboard === 'Yes') ||
        (draftWindow.type === 'Terrace door' && draftWindow.hasWaterboard === 'Yes' && drOp !== 'out');

      if (!isMountingUnderWater) {
        list.push('on-wall_roof-overhang-question');
        if (draftWindow.hasRoofOverhang === 'Yes') {
          list.push('on-wall_roof-overhang-measure');
        }
      }
      list.push('name');
    }
    return list;
  };

  const getProgress = () => {
    const list = getStepsList();
    const index = list.indexOf(step);
    if (index === -1) return 0;
    return Math.round(((index + 1) / list.length) * 100);
  };

  // Dynamic image loaders for step illustration based on selections
  const getStepImage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isAiBeta = urlParams.get('ai_beta') === 'true';

    // If they are not in Norway, but are in the AI flow, they should use the V3 detailed illustrations
    if (!isNO && !(isAiBeta && draftWindow.flowType === 'ai')) {
      switch(step) {
        case 'width': return config.assets.measureWidth;
        case 'height': return config.assets.measureHeight;
        default: return config.assets.windowUtenRamme;
      }
    }

    // NO Flow image mapping
    switch(step) {
      case 'mount':
        return config.assets.v3NoInsideFrame;
      case 'inside-frame_width':
        return config.assets.v3NoInsideFrameWidth;
      case 'inside-frame_height':
        return config.assets.v3NoInsideFrameHeight;
        
      case 'on-wall_without-waterboard-on-top_width':
        return config.assets.v3NoOnWallWithoutWaterboardOnTopWidth;
      case 'on-wall_without-waterboard-on-top_height':
        return config.assets.v3NoOnWallWithoutWaterboardOnTopHeight;
        
      case 'on-wall_with-waterboard-on-top_window-opening-out':
        return config.assets.v3NoOnWallWithWaterboardOnTopWindowOpeningOut;
      case 'on-wall_with-waterboard-on-top_heigth-from-top-window-to-waterboard':
        return config.assets.v3NoOnWallWithWaterboardOnTopHeightFromTopWindowToWaterboard;
      case 'on-wall_with-waterboard-on-top_width':
        return config.assets.v3NoOnWallWithWaterboardOnTopWidth;
      case 'on-wall_with-waterboard-on-top_height-to-top-window':
        return config.assets.v3NoOnWallWithWaterboardOnTopHeightToTopWindow;
      case 'on-wall_with-waterboard-on-top_height-to-waterboard':
        return config.assets.v3NoOnWallWithWaterboardOnTopHeightToWaterboard;
      case 'on-wall_waterboard-out-from-wall':
        return config.assets.v3NoOnWallWaterboardOutFromWall;
        
      case 'on-wall_waterboard-question':
        return config.assets.v3NoOnWallWaterboardQuestion;
      case 'on-wall_roof-overhang-question':
      case 'on-wall_roof-overhang-measure':
        return isDE ? config.assets.v3DeOnWallRoofOverhangDe : config.assets.v3NoOnWallRoofOverhang;
      case 'on-wall_sticking-out-handle-question':
        return config.assets.v3NoOnWallStickingOutHandleQuestion;
        
      case 'on-wall_sliding-door_with-waterboard-width':
        return config.assets.v3NoOnWallSlidingDoorWithWaterboardWidth;
      case 'on-wall_sliding-door_with-waterboard-heigth':
        return config.assets.v3NoOnWallSlidingDoorWithWaterboardHeight;
      case 'on-wall_sliding-door_without-waterboard-width':
        return config.assets.v3NoOnWallSlidingDoorWithoutWaterboardWidth;
      case 'on-wall_sliding-door_without-waterboard-heigth':
        return config.assets.v3NoOnWallSlidingDoorWithoutWaterboardHeight;
      case 'on-wall_sticking-out-handle-measure':
        return config.assets.v3NoOnWallStickingOutHandleMeasure;
        
      case 'on-wall_terrace-door_open-direction':
        return config.assets.v3NoOnWallTerraceDoorOpenOut;
        
      case 'on-wall_sliding-door_without-waterboard-width-1':
        return config.assets.v3NoOnWallSlidingDoorWithoutWaterboardWidth1;
      case 'on-wall_sliding-door_without-waterboard-heigth-1':
        return config.assets.v3NoOnWallSlidingDoorWithoutWaterboardHeight1;
        
      case 'on-wall_sliding-door_skyvedor-with-vanbrett-width':
        return config.assets.v3NoOnWallSlidingDoorSkyvedorWithVanbrettWidth;
      case 'on-wall_sliding-door_skyvedor-with-vanbrett-height-to-waterboard':
        return config.assets.v3NoOnWallSlidingDoorSkyvedorWithVanbrettHeightToWaterboard;
      case 'on-wall_sliding-door_waterboard-measure':
        return config.assets.v3NoOnWallSlidingDoorWaterboardMeasure;
      case 'on-wall_sliding-door_skyvedor-with-vanbrett-height-to-top-door':
        return config.assets.v3NoOnWallSlidingDoorSkyvedorWithVanbrettHeightToTopDoor;
    }
    return config.assets.windowUtenRamme;
  };

  // Card details renderer
  const getWindowCardDetails = (win, i) => {
    if (!win) return '';
    const isV2Window = win.mountType === 'Wall mounted' || win.mountType === 'Recess mounted';
    if (isV2Window) {
      const displayMount = isDE
        ? (win.mountType === 'Wall mounted' ? 'Wandmontage' : 'Nischenmontage')
        : (win.mountType === 'Wall mounted' ? 'Montert på vegg' : 'Montert i nisje');
      
      let displayType = win.type;
      if (!isDE) {
        displayType = win.type === 'Window without waterboard on top' ? 'Uten vannbrett' : 
                      win.type === 'Window with waterboard on top' ? 'Med vannbrett' : 
                      win.type === 'Sliding door' ? 'Skyvedør' : 
                      win.type === 'Terrace door' ? 'Terrassedør' : win.type;
      }

      return html`
        <div class="window-card-details" style="display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <input 
              type="text" 
              defaultValue=${win.name || ''} 
              placeholder=${isDE ? `Fenster ${i + 1}` : `Vindu ${i + 1}`}
              maxlength=${MAX_NAME_LENGTH}
              onChange=${(e) => updateWindowName(win.id, e.target.value)}
              style="margin: 0; font-size: 16px; font-weight: 600; color: rgba(var(--color-foreground), 1); border: 1px dashed rgba(var(--color-foreground), 0.15); border-radius: 4px; padding: 2px 6px; width: 100%; max-width: 100%; background: transparent; font-family: inherit; outline: none; transition: border-color 0.2s;"
            />
          </div>
          <p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${isDE ? 'Breite' : 'Bredde'} (T/M/B): ${win.widthTop || '?'} / ${win.widthMiddle || '?'} / ${win.widthBottom || '?'} mm</p>
          <p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${isDE ? 'Höhe' : 'Høyde'} (${isDE ? 'L/R' : 'V/H'}): ${win.heightLeft || '?'} / ${win.heightRight || '?'} mm</p>
          ${win.waterboardDistance ? html`<p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${isDE ? 'Abstand zum Wasserbord' : 'Avstand til vannbrett'}: ${win.waterboardDistance} mm</p>` : ''}
          ${win.handleDistance ? html`<p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${isDE ? 'Abstand zum Griff' : 'Avstand til håndtak'}: ${win.handleDistance} mm</p>` : ''}
          ${win.topToWaterboardHeight ? html`<p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${isDE ? 'Abstand vom Fenster' : 'Avstand fra vindu/dør'}: ${win.topToWaterboardHeight} mm</p>` : ''}
          ${isDE && win.revealDepth ? html`<p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">Laibungstiefe: ${win.revealDepth} mm</p>` : ''}
          <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 2px;">
            ${win.mountType ? html`
              <span style="font-size: 11px; background: rgba(var(--color-foreground), 0.05); padding: 2px 6px; border-radius: 4px;">
                ${displayMount}
              </span>
            ` : ''}
            ${win.fasteningType ? html`
              <span style="font-size: 11px; background: rgba(var(--color-foreground), 0.05); padding: 2px 6px; border-radius: 4px;">
                ${win.fasteningType === 'In the window frame' ? 'Im Fensterrahmen' : 
                  win.fasteningType === 'Sideways in the wall' ? 'Seitlich in die Wand' : win.fasteningType}
              </span>
            ` : ''}
            ${!isDE && win.type ? html`
              <span style="font-size: 11px; background: rgba(var(--color-foreground), 0.05); padding: 2px 6px; border-radius: 4px;">
                ${displayType}
              </span>
            ` : ''}
            <span class="guarantee-badge ${win.measurementGuarantee ? '' : 'inactive'}">
              <${ShieldCheckIcon} width="14" height="14" style=${{ flexShrink: 0 }} />
              ${win.measurementGuarantee ? t('100% Målegaranti aktiv', '100% Passgarantie aktiv', '100% Guarantee active') : t('Garantie ikke aktiv', 'Garantie nicht aktiv', 'Guarantee inactive')}
            </span>
          </div>
          ${(() => {
            const intrusion = getWindowOverhangObstruction(win);
            if (intrusion) {
              return html`
                <div class="warning-banner-inline" style="margin-top: 6px; padding: 8px 12px; background: #FEF2F2; border-left: 4px solid #EF4444; border-radius: 4px; font-size: 13px; color: #991B1B; line-height: 1.4;">
                  <strong>⚠️ ${t('Advarsel', 'Warnung', 'Warning')}:</strong>${' '}
                  ${t(
                    `Vinduet/døren åpner utover, og kassetten/skinnen vil stikke ${intrusion} mm ned i lysåpningen og hindre at det kan åpnes fullt ut. 100% målegaranti kan ikke gis for dette produktet.`,
                    `Das Fenster/die Tür öffnet nach außen, und die Kassette/Schiene ragt ${intrusion} mm in die Lichtöffnung hinein, was das vollständige Öffnen verhindert. Für dieses Produkt kann keine 100% Passgarantie gewährt werden.`,
                    `The window/door opens outward, and the cassette/rail will protrude ${intrusion} mm into the light opening, preventing it from opening fully. No guarantee can be provided for this product.`
                  )}
                </div>
              `;
            }
            return '';
          })()}
        </div>
      `;
    }

    // Default V1 details
    return html`
      <div class="window-card-details" style="display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0;">
        <div style="display: flex; align-items: center; gap: 6px;">
          <input 
            type="text" 
            defaultValue=${win.name || ''} 
            placeholder=${isDE ? `Fenster ${i + 1}` : `Vindu ${i + 1}`}
            maxlength=${MAX_NAME_LENGTH}
            onChange=${(e) => updateWindowName(win.id, e.target.value)}
            style="margin: 0; font-size: 16px; font-weight: 600; color: rgba(var(--color-foreground), 1); border: 1px dashed rgba(var(--color-foreground), 0.15); border-radius: 4px; padding: 2px 6px; width: 100%; max-width: 100%; background: transparent; font-family: inherit; outline: none; transition: border-color 0.2s;"
          />
        </div>
        <p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7); font-family: monospace;">Bredde (T/M/B): ${win.widthTop || '?'} / ${win.widthMiddle || '?'} / ${win.widthBottom || '?'} mm</p>
        <p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7); font-family: monospace;">Høyde (V/H): ${win.heightLeft || '?'} / ${win.heightRight || '?'} mm</p>
        ${win.type ? html`<p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${win.type === 'Uten ramme' ? 'Vindu uten ramme eller vannbord i topp' : win.type === 'Med vannbord' ? 'Vindu med vannbord og/eller omramming' : 'Skyvedør eller andre typer'}</p>` : ''}
        ${win.mountType ? html`<p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.7);">${win.mountType === 'Innvendig' ? 'Zip-Screen monteres INNI nisje' : 'Zip-Screen monteres UTENFOR nisje'}</p>` : ''}
        <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 2px;">
          <span class="guarantee-badge ${win.measurementGuarantee ? '' : 'inactive'}">
            <${ShieldCheckIcon} width="14" height="14" style=${{ flexShrink: 0 }} />
            ${win.measurementGuarantee ? t('100% Målegaranti aktiv', '100% Passgarantie aktiv', '100% Guarantee active') : t('Garantie ikke aktiv', 'Garantie nicht aktiv', 'Guarantee inactive')}
          </span>
        </div>
      </div>
    `;
  };

  const getWindowCardImage = (win) => {
    if (!win) return config.assets.windowUtenRamme || '';
    if (win.image) return win.image;
    
    if (isDE) {
      if (win.mountType === 'Recess mounted') {
        if (win.fasteningType === 'In the window frame') {
          return config.assets.v2DeInDenFensterrahmen;
        }
        if (win.fasteningType === 'Sideways in the wall') {
          return config.assets.v2DeSeitlichInDieWand;
        }
        return config.assets.v2DeInDerLaibung;
      }
      if (win.mountType === 'Wall mounted') {
        return config.assets.v2DeAnDieWand;
      }
      return config.assets.windowUtenRamme;
    }

    if (!isNO) return config.assets.windowUtenRamme;
    
    if (win.mountType === 'Recess mounted') return config.assets.v3NoInsideFrame;
    if (win.type === 'Window without waterboard on top') return config.assets.v3NoOnWallWindowWithoutWaterboardOnTop;
    if (win.type === 'Window with waterboard on top') return config.assets.v3NoOnWallWindowWithWaterboardOnTop;
    if (win.type === 'Sliding door') return config.assets.v3NoOnWallSlidingDoor;
    if (win.type === 'Terrace door') return config.assets.v3NoOnWallTerraceDoor;
    
    return config.assets.windowUtenRamme;
  };

  const getToolThumbnail = (tool) => {
    if (tool === 'Measuring tape') return config.assets.toolMeasuringTape;
    if (tool === 'Folding rule') return config.assets.toolZollstock;
    if (tool === 'Laser') return config.assets.toolLaser;
    return null;
  };

  // Version 3 NO Generic render helpers
  const renderWidthStepGeneric = (title, desc) => {
    const isCompleted = draftWindow.widthTop && draftWindow.widthMiddle && draftWindow.widthBottom;
    const widthTooSmall = [draftWindow.widthTop, draftWindow.widthMiddle, draftWindow.widthBottom]
      .some(v => v !== undefined && v !== '' && Number(v) < MIN_WIDTH_MM);
    return html`
      <div class="measure-container">
        <div class="measure-header">
          <h1>${title}</h1>
          ${desc ? html`<p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">${desc}</p>` : ''}
        </div>
        ${renderProgressBar()}
        <div class="measure-content">
          <div class="instruction-image">
            <img src=${getStepImage()} alt=${title} />
          </div>
          <div class="input-group" style="margin-top: 24px;">
            <label class="field-label">${t('1. Bredde topp (mm)', 'Oben (mm)', 'Top (mm)')}</label>
            <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder=${t('F.eks. 3000', 'z. B. 3000', 'e.g. 3000')} value=${draftWindow.widthTop} 
                   onInput=${e => setDraftWindow({...draftWindow, widthTop: e.target.value})} />
          </div>
          <div class="input-group">
            <label class="field-label">${t('2. Bredde midten (mm)', 'Mitte (mm)', 'Middle (mm)')}</label>
            <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder=${t('F.eks. 3002', 'z. B. 3002', 'e.g. 3002')} value=${draftWindow.widthMiddle} 
                   onInput=${e => setDraftWindow({...draftWindow, widthMiddle: e.target.value})} />
          </div>
          <div class="input-group">
            <label class="field-label">${t('3. Bredde bunn (mm)', 'Unten (mm)', 'Bottom (mm)')}</label>
            <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder=${t('F.eks. 3005', 'z. B. 3005', 'e.g. 3005')} value=${draftWindow.widthBottom}
                   onInput=${e => setDraftWindow({...draftWindow, widthBottom: e.target.value})} />
          </div>
          ${widthTooSmall ? html`
            <div class="warning-banner-inline" style="margin-top: 12px; padding: 8px 12px; background: #FEF2F2; border-left: 4px solid #EF4444; border-radius: 4px; font-size: 13px; color: #991B1B; line-height: 1.4;">
              <strong>⚠️ ${t('Advarsel', 'Warnung', 'Warning')}:</strong>${' '}
              ${t(
                `Vår minimumsbredde er ${MIN_WIDTH_MM} mm. Vennligst kontroller målet ditt.`,
                `Unsere Mindestbreite beträgt ${MIN_WIDTH_MM} mm. Bitte prüfe deine Messung.`,
                `Our minimum width is ${MIN_WIDTH_MM} mm. Please double-check your measurement.`
              )}
            </div>
          ` : ''}
        </div>
        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
          <button class="button--brand"
                  disabled=${!isCompleted}
                  onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
        </div>
      </div>
    `;
  };

  const renderHeightStepGeneric = (title, desc) => {
    const isCompleted = draftWindow.heightLeft && draftWindow.heightRight;
    const heightTooSmall = [draftWindow.heightLeft, draftWindow.heightRight]
      .some(v => v !== undefined && v !== '' && Number(v) < MIN_HEIGHT_MM);
    return html`
      <div class="measure-container">
        <div class="measure-header">
          <h1>${title}</h1>
          ${desc ? html`<p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">${desc}</p>` : ''}
        </div>
        ${renderProgressBar()}
        <div class="measure-content">
          <div class="instruction-image">
            <img src=${getStepImage()} alt=${title} />
          </div>
          <div class="input-group" style="margin-top: 24px;">
            <label class="field-label">${t('1. Høyde venstre (mm)', 'Links (mm)', 'Left (mm)')}</label>
            <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder=${t('F.eks. 2100', 'z. B. 2100', 'e.g. 2100')} value=${draftWindow.heightLeft} 
                   onInput=${e => setDraftWindow({...draftWindow, heightLeft: e.target.value})} />
          </div>
          <div class="input-group">
            <label class="field-label">${t('2. Høyde høyre (mm)', 'Rechts (mm)', 'Right (mm)')}</label>
            <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder=${t('F.eks. 2100', 'z. B. 2100', 'e.g. 2100')} value=${draftWindow.heightRight}
                   onInput=${e => setDraftWindow({...draftWindow, heightRight: e.target.value})} />
          </div>
          ${heightTooSmall ? html`
            <div class="warning-banner-inline" style="margin-top: 12px; padding: 8px 12px; background: #FEF2F2; border-left: 4px solid #EF4444; border-radius: 4px; font-size: 13px; color: #991B1B; line-height: 1.4;">
              <strong>⚠️ ${t('Advarsel', 'Warnung', 'Warning')}:</strong>${' '}
              ${t(
                `Vår minimumshøyde er ${MIN_HEIGHT_MM} mm. Vennligst kontroller målet ditt.`,
                `Unsere Mindesthöhe beträgt ${MIN_HEIGHT_MM} mm. Bitte prüfe deine Messung.`,
                `Our minimum height is ${MIN_HEIGHT_MM} mm. Please double-check your measurement.`
              )}
            </div>
          ` : ''}
        </div>
        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
          <button class="button--brand"
                  disabled=${!isCompleted}
                  onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
        </div>
      </div>
    `;
  };

  const renderSingleInputStepGeneric = (title, desc, field, placeholder) => {
    const isCompleted = draftWindow[field] !== undefined && draftWindow[field] !== '';
    return html`
      <div class="measure-container">
        <div class="measure-header">
          <h1>${title}</h1>
          ${desc ? html`<p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">${desc}</p>` : ''}
        </div>
        ${renderProgressBar()}
        <div class="measure-content">
          <div class="instruction-image">
            <img src=${getStepImage()} alt=${title} />
          </div>
          <div class="input-group" style="margin-top: 24px;">
            <label class="field-label">${t('Mål (mm)', 'Maß (mm)', 'Measure (mm)')}</label>
            <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder=${placeholder} value=${draftWindow[field]} 
                   onInput=${e => setDraftWindow({...draftWindow, [field]: e.target.value})} />
          </div>
        </div>
        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
          <button class="button--brand" 
                  disabled=${!isCompleted}
                  onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
        </div>
      </div>
    `;
  };

  const renderWindowOpeningOutQ = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Slår vinduet utover?', 'Öffnet das Fenster nach außen?', 'Does the window open outwards?')}</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">
          ${t(
            'Velg om vinduet åpnes utover eller innover/er fast.',
            'Wähle aus, ob das Fenster nach außen oder nach innen öffnet / feststehend ist.',
            'Select if the window opens outwards or inwards/is fixed.'
          )}
        </p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Vindusåpning" />
        </div>
        <div class="options-grid" style="margin-top: 24px;">
          <div class="option-card ${draftWindow.windowOpeningOut === 'Yes' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, windowOpeningOut: 'Yes'})}>
            <h3 style="text-align: center; margin: 24px 0;">
              ${t('Ja, vinduet slår utover', 'Ja, das Fenster öffnet nach außen', 'Yes, the window opens outwards')}
            </h3>
          </div>
          <div class="option-card ${draftWindow.windowOpeningOut === 'No' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, windowOpeningOut: 'No'})}>
            <h3 style="text-align: center; margin: 24px 0;">
              ${t('Nei, det slår innover eller er fast', 'Nein, es öffnet nach innen oder ist feststehend', 'No, it opens inwards or is fixed')}
            </h3>
          </div>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                disabled=${!draftWindow.windowOpeningOut}
                onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
      </div>
    </div>
  `;

  const renderTerraceDoorOpenDirectionQ = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Slår døren utover eller innover?', 'Öffnet die Tür nach außen oder nach innen?', 'Does the door open outwards or inwards?')}</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">
          ${t('Velg åpningsretningen for terrassedøren.', 'Wähle die Öffnungsrichtung für die Terrassentür.', 'Select the opening direction for the terrace door.')}
        </p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.doorOpening === 'out' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, doorOpening: 'out'})}>
          <img src=${config.assets.v3NoOnWallTerraceDoorOpenOut} alt="Utadslående" />
          <h3>${t('Utadslående (døren slår utover)', 'Nach außen öffnend (Tür schlägt nach außen)', 'Outward opening (door opens outwards)')}</h3>
        </div>
        <div class="option-card ${draftWindow.doorOpening === 'in' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, doorOpening: 'in'})}>
          <img src=${config.assets.v3NoOnWallTerraceDoorOpenIn} alt="Innadslående" />
          <h3>${t('Innadslående (døren slår innover)', 'Nach innen öffnend (Tür schlägt nach innen)', 'Inward opening (door opens inwards)')}</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                disabled=${!draftWindow.doorOpening}
                onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
      </div>
    </div>
  `;

  const renderTypeSelectionV3 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Velg vindustype', 'Fenstertyp wählen', 'Choose window type')}</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">
          ${t('Velg utførelsen som passer best for vinduet ditt.', 'Wähle die Ausführung, die am besten zu deinem Fenster passt.', 'Select the option that best fits your window.')}
        </p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.type === 'Window without waterboard on top' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               type: 'Window without waterboard on top',
               waterboardDistance: '',
               handleDistance: '',
               hasWaterboard: 'No',
               hasHandle: 'No',
               windowOpeningOut: '',
               doorOpening: '',
               topToWaterboardHeight: ''
             })}>
          <img src=${config.assets.v3NoOnWallWindowWithoutWaterboardOnTop} alt="Vindu uten vannbrett" />
          <h3>${t('Vindu uten vannbrett i toppen', 'Fenster ohne obere Fensterbank', 'Window without waterboard on top')}</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Window with waterboard on top' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               type: 'Window with waterboard on top',
               waterboardDistance: '',
               handleDistance: '',
               hasWaterboard: 'Yes',
               hasHandle: 'No',
               windowOpeningOut: '',
               doorOpening: '',
               topToWaterboardHeight: ''
             })}>
          <img src=${config.assets.v3NoOnWallWindowWithWaterboardOnTop} alt="Vindu med vannbrett" />
          <h3>${t('Vindu med vannbrett i toppen', 'Fenster mit oberer Fensterbank', 'Window with waterboard on top')}</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Sliding door' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               type: 'Sliding door',
               waterboardDistance: '',
               handleDistance: '',
               hasWaterboard: '',
               hasHandle: '',
               windowOpeningOut: '',
               doorOpening: '',
               topToWaterboardHeight: ''
             })}>
          <img src=${config.assets.v3NoOnWallSlidingDoor} alt="Skyvedør" />
          <h3>${t('Skyvedør', 'Schiebetür', 'Sliding door')}</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Terrace door' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               type: 'Terrace door',
               waterboardDistance: '',
               handleDistance: '',
               hasWaterboard: '',
               hasHandle: '',
               windowOpeningOut: '',
               doorOpening: '',
               topToWaterboardHeight: ''
             })}>
          <img src=${config.assets.v3NoOnWallTerraceDoor} alt="Terrassedør" />
          <h3>${t('Terrassedør', 'Terrassentür', 'Terrace door')}</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                disabled=${!draftWindow.type}
                onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
      </div>
    </div>
  `;

  // Flow Selection step
  const renderFlowSelection = () => {
    const handleFlowChoose = (type) => {
      setDraftWindow({ ...draftWindow, flowType: type });
      if (type === 'ai') {
        setStep('aiUpload');
      } else {
        setStep('mount');
      }
    };

    return html`
      <div class="measure-container">
        <div class="measure-header" style="margin-bottom: 24px;">
          <h1>${t('Velg målemetode', 'Messmethode wählen', 'Choose measuring method')}</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); line-height: 1.5; margin-top: 12px; text-align: left;">
            ${t(
              'Hvordan vil du legge til detaljene for vinduet ditt?',
              'Wie möchtest du die Details für dein Fenster hinzufügen?',
              'How would you like to add the details for your window?'
            )}
          </p>
        </div>

        <div class="measure-content" style="display: flex; flex-direction: column; gap: 16px;">
          <!-- Manual Option -->
          <div class="selection-card" style="border: 1px solid rgba(var(--color-foreground), 0.15); border-radius: 12px; padding: 20px; cursor: pointer; transition: all 0.2s ease; background: white;" 
               onClick=${() => handleFlowChoose('manual')}
               onMouseEnter=${(e) => { e.currentTarget.style.borderColor = '#5B5BF9'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(91,91,249,0.08)'; }}
               onMouseLeave=${(e) => { e.currentTarget.style.borderColor = 'rgba(var(--color-foreground), 0.15)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style="display: flex; gap: 16px; align-items: center;">
              <div style="display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 50%; background: rgba(var(--color-foreground), 0.05); color: rgba(var(--color-foreground), 0.7);">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                </svg>
              </div>
              <div style="flex: 1; text-align: left;">
                <h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600;">
                  ${t('Mål steg-for-steg (Manuell)', 'Schritt für Schritt messen (Manuell)', 'Measure step-by-step (Manual)')}
                </h3>
                <p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.6); line-height: 1.4;">
                  ${t(
                    'Vi veileder deg gjennom enkle spørsmål om montering og vindusdetaljer.',
                    'Wir führen dich durch einfache Fragen zur Montage und zu Fensterdetails.',
                    'We guide you through simple questions about mounting and window details.'
                  )}
                </p>
              </div>
            </div>
          </div>

          <!-- AI Option -->
          <div class="selection-card" style="border: 1px solid rgba(var(--color-foreground), 0.15); border-radius: 12px; padding: 20px; cursor: pointer; transition: all 0.2s ease; background: white; position: relative;" 
               onClick=${() => handleFlowChoose('ai')}
               onMouseEnter=${(e) => { e.currentTarget.style.borderColor = '#0EA674'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,166,116,0.08)'; }}
               onMouseLeave=${(e) => { e.currentTarget.style.borderColor = 'rgba(var(--color-foreground), 0.15)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style="position: absolute; top: -10px; right: 16px; background: #0EA674; color: white; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 12px; text-transform: uppercase;">
              ${t('Nyhet!', 'Neu!', 'New!')}
            </div>
            <div style="display: flex; gap: 16px; align-items: center;">
              <div style="display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 50%; background: rgba(14, 166, 116, 0.1); color: #0EA674;">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                  <circle cx="12" cy="13" r="4"></circle>
                </svg>
              </div>
              <div style="flex: 1; text-align: left;">
                <h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600; color: #0EA674;">
                  ${t('Hurtigskann med bilde (Smart)', 'Schnellscan mit Bild (Smart)', 'Fast scan with photo (Smart)')}
                </h3>
                <p style="margin: 0; font-size: 14px; color: rgba(var(--color-foreground), 0.6); line-height: 1.4;">
                  ${t(
                    'Ta et bilde av vinduet. Vår smarte skanner finner detaljene automatisk, og du slipper spørsmålene.',
                    'Mache ein Foto vom Fenster. Unser smarter Scanner erkennt Details automatisch und überspringt die Fragen.',
                    'Take a photo of the window. Our smart scanner detects details automatically, skipping the questions.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="measure-footer">
          <button class="button--brand-secondary" style="width: 100%;" onClick=${() => setStep('overview')}>
            ${t('Avbryt', 'Abbrechen', 'Cancel')}
          </button>
        </div>
      </div>
    `;
  };

  // AI Upload step
  const renderAiUpload = () => {
    const [localPreview, setLocalPreview] = useState(draftWindow.image || null);
    const [isCompressing, setIsCompressing] = useState(false);

    const onFileSelect = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setIsCompressing(true);
      compressImage(
        file,
        (compressed, raw) => {
          setLocalPreview(raw);
          setDraftWindow(prev => ({ ...prev, image: compressed }));
          setIsCompressing(false);
        },
        (err) => {
          console.error("AI image compression failed:", err);
          setIsCompressing(false);
        }
      );
      e.target.value = '';
    };

    const handleScanClick = () => {
      if (draftWindow.image && !isCompressing) {
        setStep('aiScanning');
      }
    };

    return html`
      <div class="measure-container">
        <div class="measure-header" style="margin-bottom: 24px;">
          <h1>${t('Ta eller last opp bilde', 'Foto aufnehmen oder hochladen', 'Take or upload photo')}</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); line-height: 1.5; margin-top: 12px; text-align: left;">
            ${t(
              'Ta et bilde rett forfra av vinduet eller døren. Sørg for at hele vinduet, rammen og eventuelle vannbrett er godt synlig.',
              'Mache ein Foto frontal vor dem Fenster oder der Tür. Stelle sicher, dass das gesamte Fenster, der Rahmen und eventuelle Fensterbänke gut sichtbar sind.',
              'Take a photo straight in front of the window or door. Make sure the entire window, frame, and any waterboards are fully visible.'
            )}
          </p>
        </div>

        <div class="measure-content" style="display: flex; flex-direction: column; align-items: center; gap: 20px;">
          <div style="width: 100%; max-width: 400px; aspect-ratio: 4/3; border: 2px dashed rgba(var(--color-foreground), 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center; overflow: hidden; background: rgba(var(--color-foreground), 0.02); position: relative;">
            ${isCompressing ? html`
              <div style="display: flex; flex-direction: column; align-items: center; gap: 12px;">
                <svg class="loading-spin-svg" style="animation: spin 1s infinite linear;" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <circle cx="12" cy="12" r="10" stroke="rgba(var(--color-foreground), 0.1)"></circle>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#0EA674"></path>
                </svg>
                <span style="font-size: 14px; color: rgba(var(--color-foreground), 0.6); font-weight: 500;">
                  ${t('Behandler bilde...', 'Bild wird verarbeitet...', 'Processing image...')}
                </span>
              </div>
            ` : (localPreview ? html`
              <img src=${localPreview} alt="Preview" style="width: 100%; height: 100%; object-fit: cover;" />
              <label style="position: absolute; bottom: 12px; right: 12px; background: rgba(0,0,0,0.6); color: white; padding: 6px 12px; border-radius: 20px; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: 500;">
                <${UploadIcon} />
                ${t('Endre bilde', 'Bild ändern', 'Change photo')}
                <input type="file" accept="image/*" style="display: none;" onChange=${onFileSelect} />
              </label>
            ` : html`
              <label style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; gap: 12px; padding: 24px; text-align: center;">
                <div style="width: 56px; height: 56px; border-radius: 50%; background: rgba(14,166,116,0.1); color: #0EA674; display: flex; align-items: center; justify-content: center;">
                  <${UploadIcon} />
                </div>
                <div>
                  <strong style="display: block; font-size: 16px; margin-bottom: 4px; color: #0EA674; text-align: center;">
                    ${t('Ta eller velg bilde', 'Foto aufnehmen oder wählen', 'Take or select photo')}
                  </strong>
                  <span style="font-size: 13px; color: rgba(var(--color-foreground), 0.5); text-align: center; display: block;">
                    ${t('Trykk her for å bruke kamera eller filer', 'Klicke hier, um Kamera oder Dateien zu nutzen', 'Click here to use camera or files')}
                  </span>
                </div>
                <input type="file" accept="image/*" style="display: none;" onChange=${onFileSelect} />
              </label>
            `)}
          </div>
        </div>

        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${() => setStep('flowSelection')}>
            ${t('Tilbake', 'Zurück', 'Back')}
          </button>
          <button class="button--brand" disabled=${!draftWindow.image || isCompressing} onClick=${handleScanClick}>
            ${isCompressing 
              ? t('Behandler bilde...', 'Bild wird verarbeitet...', 'Processing image...') 
              : t('Skann vindu', 'Fenster scannen', 'Scan window')}
          </button>
        </div>
      </div>
    `;
  };

  // AI Scanning step
  const renderAiScanning = () => {
    const [statusIndex, setStatusIndex] = useState(0);
    const [error, setError] = useState(null);

    const statusMessages = [
      t('Laster opp bilde...', 'Bild wird hochgeladen...', 'Uploading photo...'),
      t('Analyserer karm og nisje...', 'Rahmen und Nische werden analysiert...', 'Analyzing frame and recess...'),
      t('Ser etter vannbrett og hindringer...', 'Fensterbänke und Hindernisse werden gesucht...', 'Searching for waterboards and obstacles...'),
      t('Sjekker åpningsretning...', 'Öffnungsrichtung wird geprüft...', 'Checking opening direction...'),
      t('Fullfører skanning...', 'Scan wird abgeschlossen...', 'Finalizing scan...')
    ];

    useEffect(() => {
      const interval = setInterval(() => {
        setStatusIndex(prev => (prev < statusMessages.length - 1 ? prev + 1 : prev));
      }, 950);

      const performScan = async () => {
        try {
          const response = await fetch('/apps/measurements?action=analyze-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              image: draftWindow.image
            })
          });

          if (!response.ok) {
            const errData = await response.json();
            throw new Error(errData.error || t('Kunne ikke koble til serveren.', 'Serververbindung fehlgeschlagen.', 'Failed to connect to the server.'));
          }

          const result = await response.json();

          const detectedType = result.type || 'Window without waterboard on top';
          const defaultMount = ['Sliding door', 'Terrace door'].includes(detectedType) ? 'Wall mounted' : 'Recess mounted';
          const detectedMount = ['Recess mounted', 'Wall mounted'].includes(result.mountType) ? result.mountType : defaultMount;

          setDraftWindow(prev => ({
            ...prev,
            mountType: detectedMount,
            type: detectedType,
            windowOpeningOut: result.openingDirection === 'out' ? 'Yes' : (result.openingDirection === 'in' ? 'No' : ''),
            doorOpening: ['in', 'out'].includes(result.openingDirection) ? result.openingDirection : '',
            hasWaterboard: ['Yes', 'No'].includes(result.hasWaterboard) ? result.hasWaterboard : '',
            hasHandle: ['Yes', 'No'].includes(result.hasHandle) ? result.hasHandle : ''
          }));

          setStep('aiConfirm');
        } catch (err) {
          console.error("AI scanning failed:", err);
          setError(err.message || t('Det oppstod en feil under skanningen.', 'Ein Fehler ist beim Scannen aufgetreten.', 'An error occurred during scanning.'));
        }
      };

      performScan();

      return () => {
        clearInterval(interval);
      };
    }, []);

    if (error) {
      return html`
        <div class="measure-container">
          <div class="measure-header" style="margin-bottom: 24px;">
            <h1 style="color: #DC2626;">${t('Skanning mislyktes', 'Scan fehlgeschlagen', 'Scanning failed')}</h1>
            <p style="color: rgba(var(--color-foreground), 0.7); line-height: 1.5; margin-top: 12px; text-align: left;">
              ${error}
            </p>
          </div>

          <div class="measure-content" style="display: flex; flex-direction: column; align-items: center; gap: 20px; padding-top: 0;">
            <div style="width: 56px; height: 56px; border-radius: 50%; background: rgba(220, 38, 38, 0.1); color: #DC2626; display: flex; align-items: center; justify-content: center;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            
            <button class="text-btn" style="color: rgba(var(--color-foreground), 0.6); font-weight: 500;" onClick=${() => {
              setDraftWindow({ ...draftWindow, flowType: 'manual' });
              setStep('mount');
            }}>
              ${t('Fortsett med manuell måling', 'Mit manueller Messung fortfahren', 'Continue with manual measuring')}
            </button>
          </div>

          <div class="measure-footer measure-footer-buttons">
            <button class="button--brand-secondary" onClick=${() => {
              setDraftWindow(prev => ({ ...prev, image: null }));
              setStep('aiUpload');
            }}>
              ${t('Velg annet bilde', 'Anderes Bild wählen', 'Choose other photo')}
            </button>
            <button class="button--brand" onClick=${() => { setError(null); setStep('aiScanning'); }}>
              ${t('Prøv igjen', 'Erneut versuchen', 'Try again')}
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="measure-container">
        <style>
          @keyframes laserScan {
            0% { top: 0%; }
            50% { top: 100%; }
            100% { top: 0%; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
        <div class="measure-header" style="margin-bottom: 24px;">
          <h1>${t('Skanner vindu...', 'Fenster wird gescannt...', 'Scanning window...')}</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); font-size: 15px; margin-top: 8px; text-align: left;">
            ${statusMessages[statusIndex]}
          </p>
        </div>

        <div class="measure-content" style="display: flex; flex-direction: column; align-items: center; gap: 24px;">
          <div style="width: 100%; max-width: 400px; aspect-ratio: 4/3; border-radius: 12px; overflow: hidden; position: relative; background: #000; box-shadow: 0 8px 24px rgba(0,0,0,0.15);">
            <img src=${draftWindow.image} alt="" style="width: 100%; height: 100%; object-fit: cover; opacity: 0.85;" />
            <div class="scan-laser-line" style="position: absolute; left: 0; width: 100%; height: 4px; background: linear-gradient(180deg, rgba(14,166,116,0) 0%, #0EA674 50%, rgba(14,166,116,0) 100%); box-shadow: 0 0 12px #0EA674; animation: laserScan 2.5s infinite ease-in-out;"></div>
            <div style="position: absolute; inset: 0; background: linear-gradient(180deg, rgba(14,166,116,0.05) 0%, rgba(14,166,116,0) 100%); pointer-events: none;"></div>
          </div>

          <div class="scanning-spinner" style="display: inline-flex; align-items: center; gap: 12px; font-size: 14px; color: rgba(var(--color-foreground), 0.6); font-weight: 500;">
            <svg class="loading-spin-svg" style="animation: spin 1s infinite linear;" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <circle cx="12" cy="12" r="10" stroke="rgba(var(--color-foreground), 0.1)"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="#0EA674"></path>
            </svg>
            <span>${t('Arbeider...', 'Verarbeite...', 'Processing...')}</span>
          </div>
        </div>
      </div>
    `;
  };

  // AI Confirm step
  const renderAiConfirm = () => {
    const handleConfirm = () => {
      const list = getStepsList();
      const nextIdx = list.indexOf('aiConfirm') + 1;
      if (nextIdx < list.length) {
        setStep(list[nextIdx]);
      } else {
        setStep('name');
      }
    };

    const isConfirmDisabled = () => {
      if (!draftWindow.mountType || !draftWindow.type) return true;
      
      // If it's a terrace door, recess-mounted AND opens outwards, disable because it's physically impossible/invalid
      if (draftWindow.type === 'Terrace door' && draftWindow.mountType === 'Recess mounted' && draftWindow.doorOpening === 'out') {
        return true;
      }
      
      if (draftWindow.type === 'Terrace door') {
        if (!draftWindow.doorOpening) return true;
        if (!draftWindow.hasWaterboard) return true;
        if (!draftWindow.hasHandle) return true;
      }
      
      if (draftWindow.type === 'Sliding door') {
        if (!draftWindow.hasWaterboard) return true;
        if (!draftWindow.hasHandle) return true;
      }
      
      if (draftWindow.mountType === 'Wall mounted' && draftWindow.type === 'Window with waterboard on top') {
        if (!draftWindow.windowOpeningOut) return true;
      }
      
      return false;
    };

    return html`
      <div class="measure-container">
        <div class="measure-header" style="margin-bottom: 24px;">
          <h1>${t('Bekreft vindusdetaljer', 'Details bestätigen', 'Confirm window details')}</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); line-height: 1.5; margin-top: 12px; text-align: left;">
            ${t(
              'Vi skannet bildet og fant disse detaljene. Sjekk at alt stemmer og gjør eventuelle endringer før du går videre til måling.',
              'Wir haben das Bild gescannt und diese Details erkannt. Bitte überprüfe sie und nimm eventuelle Korrekturen vor, bevor du misst.',
              'We scanned the photo and detected these details. Please review them and make corrections if needed before proceeding.'
            )}
          </p>
        </div>

        <div class="measure-content" style="display: flex; flex-direction: column; gap: 24px; align-items: center;">
          <div style="width: 100%; max-width: 400px; aspect-ratio: 4/3; border-radius: 12px; overflow: hidden; border: 1px solid rgba(var(--color-foreground), 0.15); box-shadow: 0 4px 12px rgba(0,0,0,0.06);">
            <img src=${draftWindow.image} alt="" style="width: 100%; height: 100%; object-fit: cover;" />
          </div>

          <div style="width: 100%; max-width: 400px; display: flex; flex-direction: column; gap: 16px; background: rgba(var(--color-foreground), 0.02); padding: 20px; border-radius: 12px; border: 1px solid rgba(var(--color-foreground), 0.08); text-align: left;">
            <div style="display: flex; flex-direction: column; gap: 6px;">
              <label style="font-size: 13px; font-weight: 600; color: rgba(var(--color-foreground), 0.6);">${t('Montering', 'Montage', 'Mounting')}</label>
              <select style="width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(var(--color-foreground), 0.15); background: white; font-size: 14px;"
                      value=${draftWindow.mountType}
                      onChange=${(e) => setDraftWindow({ ...draftWindow, mountType: e.target.value })}>
                <option value="Recess mounted">${t('Montert i nisje (Innvendig)', 'In der Nische montiert', 'Recess mounted (Inside)')}</option>
                <option value="Wall mounted">${t('Montert på vegg (Utvendig)', 'Auf der Wand montiert', 'Wall mounted (On wall)')}</option>
              </select>
            </div>

            ${['Sliding door', 'Terrace door'].includes(draftWindow.type) && draftWindow.mountType === 'Recess mounted' ? html`
              <div style="display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; background: #FFFBEB; border-radius: 8px; border: 1px solid #FCD34D; margin-top: -8px;">
                <span style="font-size: 16px; line-height: 1;">⚠️</span>
                <span style="font-size: 12px; color: #92400E; font-weight: 500; line-height: 1.4;">
                  ${t('Veggmontering anbefales for dører (hindrer at skjermen blokkerer døråpning).', 'Wandmontage wird für Türen empfohlen.', 'Wall mounting is recommended for doors.')}
                </span>
              </div>
            ` : ''}

            <div style="display: flex; flex-direction: column; gap: 6px;">
              <label style="font-size: 13px; font-weight: 600; color: rgba(var(--color-foreground), 0.6);">${t('Type', 'Typ', 'Type')}</label>
              <select style="width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(var(--color-foreground), 0.15); background: white; font-size: 14px;"
                      value=${draftWindow.type}
                      onChange=${(e) => setDraftWindow({ ...draftWindow, type: e.target.value })}>
                <option value="Window without waterboard on top">${t('Vindu uten vannbrett på topp', 'Fenster ohne obere Fensterbank', 'Window without waterboard on top')}</option>
                <option value="Window with waterboard on top">${t('Vindu med vannbrett på topp', 'Fenster mit oberer Fensterbank', 'Window with waterboard on top')}</option>
                <option value="Sliding door">${t('Skyvedør', 'Schiebetür', 'Sliding door')}</option>
                <option value="Terrace door">${t('Terrassedør', 'Terrassentür', 'Terrace door')}</option>
              </select>
            </div>

            ${draftWindow.type === 'Terrace door' || (draftWindow.mountType === 'Wall mounted' && draftWindow.type === 'Window with waterboard on top') ? html`
              <div style="display: flex; flex-direction: column; gap: 6px;">
                <label style="font-size: 13px; font-weight: 600; color: rgba(var(--color-foreground), 0.6);">${t('Åpningsretning', 'Öffnungsrichtung', 'Opening direction')}</label>
                <select style="width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(var(--color-foreground), 0.15); background: white; font-size: 14px;"
                        value=${draftWindow.type === 'Terrace door' ? draftWindow.doorOpening : (draftWindow.windowOpeningOut === 'Yes' ? 'out' : (draftWindow.windowOpeningOut === 'No' ? 'in' : ''))}
                        onChange=${(e) => {
                          const val = e.target.value;
                          if (draftWindow.type === 'Terrace door') {
                            setDraftWindow({ 
                              ...draftWindow, 
                              doorOpening: val,
                              windowOpeningOut: val === 'out' ? 'Yes' : 'No'
                            });
                          } else {
                            setDraftWindow({
                              ...draftWindow,
                              windowOpeningOut: val === 'out' ? 'Yes' : 'No'
                            });
                          }
                        }}>
                  <option value="">${t('-- Velg åpningsretning --', '-- Öffnungsrichtung wählen --', '-- Select opening direction --')}</option>
                  <option value="in">${t('Slår innover (innadslående)', 'Nach innen öffnend', 'Opens inwards')}</option>
                  <option value="out">${t('Slår utover (utadslående)', 'Nach außen öffnend', 'Opens outwards')}</option>
                </select>
              </div>
            ` : ''}

            ${draftWindow.type === 'Terrace door' && draftWindow.doorOpening === 'out' && draftWindow.mountType === 'Recess mounted' ? html`
              <div style="display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; background: #FEF2F2; border-radius: 8px; border: 1px solid #FCA5A5; margin-top: -8px;">
                <span style="font-size: 16px; line-height: 1;">❌</span>
                <span style="font-size: 12px; color: #991B1B; font-weight: 500; line-height: 1.4;">
                  ${t('Utadslående dør montert i nisje er ikke mulig (velg Veggmontering).', 'Nach außen öffnende Tür in der Nische ist nicht möglich (Wandmontage wählen).', 'Outward-opening door cannot be recess-mounted (choose Wall mounted).')}
                </span>
              </div>
            ` : ''}

            ${['Sliding door', 'Terrace door'].includes(draftWindow.type) ? html`
              <div style="display: flex; flex-direction: column; gap: 6px;">
                <label style="font-size: 13px; font-weight: 600; color: rgba(var(--color-foreground), 0.6);">${t('Har vannbrett på topp?', 'Obere Fensterbank vorhanden?', 'Has waterboard on top?')}</label>
                <select style="width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(var(--color-foreground), 0.15); background: white; font-size: 14px;"
                        value=${draftWindow.hasWaterboard}
                        onChange=${(e) => setDraftWindow({ ...draftWindow, hasWaterboard: e.target.value })}>
                  <option value="">${t('-- Velg vannbrett --', '-- Fensterbank wählen --', '-- Select waterboard --')}</option>
                  <option value="Yes">${t('Ja, har vannbrett i toppen', 'Ja, hat obere Fensterbank', 'Yes, has waterboard on top')}</option>
                  <option value="No">${t('Nei, har ikke vannbrett', 'Nein, hat keine obere Fensterbank', 'No, does not have waterboard')}</option>
                </select>
              </div>

              <div style="display: flex; flex-direction: column; gap: 6px;">
                <label style="font-size: 13px; font-weight: 600; color: rgba(var(--color-foreground), 0.6);">${t('Stikker dørhåndtak ut?', 'Türgriff steht über?', 'Door handle protrudes?')}</label>
                <select style="width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(var(--color-foreground), 0.15); background: white; font-size: 14px;"
                        value=${draftWindow.hasHandle}
                        onChange=${(e) => setDraftWindow({ ...draftWindow, hasHandle: e.target.value })}>
                  <option value="">${t('-- Velg dørhåndtak --', '-- Türgriff wählen --', '-- Select door handle --')}</option>
                  <option value="Yes">${t('Ja, håndtaket stikker ut', 'Ja, Griff steht über', 'Yes, handle protrudes')}</option>
                  <option value="No">${t('Nei, flatt eller stikker ikke ut', 'Nein, flach oder steht nicht über', 'No, flat or does not protrude')}</option>
                </select>
              </div>
            ` : ''}
          </div>
        </div>

        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${() => {
            setDraftWindow(prev => ({ ...prev, image: null }));
            setStep('aiUpload');
          }}>
            ${t('Ta nytt bilde', 'Neues Foto machen', 'Take new photo')}
          </button>
          <button class="button--brand" disabled=${isConfirmDisabled()} onClick=${handleConfirm}>
            ${t('Bekreft og start måling', 'Bestätigen und Messung starten', 'Confirm and start measuring')}
          </button>
        </div>
      </div>
    `;
  };

  // Step renderers
  const renderStart = () => html`
    <div class="measure-container">
      <div class="measure-card intro-card">
        <div class="ruler-icon-wrapper"><${RulerIcon} /></div>
        <div class="intro-header" style="text-align: center;">
          <h1 style="margin-bottom: 0;">${t('Mål vinduene dine', 'Fenster ausmessen', 'Measure your windows')}</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); line-height: 1.5; margin-top: 12px; margin-bottom: 0;">
            ${t(
              'Få den perfekte passformen for dine zip-screens med noen få enkle trinn. Vi veileder deg gjennom målingen av hvert vindu for å sikre riktige mål.',
              'Miss deine Fenster in wenigen einfachen Schritten aus. Wir führen dich Schritt für Schritt durch, um die perfekten Maße für deine Zipscreens zu ermitteln.',
              'Get the perfect fit for your screens in just a few simple steps. We guide you through measuring each window to ensure correct dimensions.'
            )}
          </p>
          <div class="guarantee-info-card" style="background: rgba(14, 166, 116, 0.03); border: 1px solid rgba(14, 166, 116, 0.2); border-radius: 12px; padding: 16px; margin-top: 14px; margin-bottom: 0; display: flex; gap: 12px; align-items: flex-start; text-align: left; width: 100%;">
            <${ShieldCheckIcon} width="20" height="20" style=${{ color: '#0EA674', flexShrink: 0, marginTop: '2px' }} />
            <div style="flex: 1;">
              <h4 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #0EA674;">
                ${t('100% Målegaranti inkludert', '100% Passgarantie inklusive', '100% Fit Guarantee included')}
              </h4>
              <p style="margin: 0; font-size: 15px; color: rgba(var(--color-foreground), 0.7); line-height: 1.5;">
                ${t(
                  'Ved å bruke appen får du 100% målegaranti. Hvis målene ikke stemmer, ordner vi det uten ekstra kostnad.',
                  'Durch die Nutzung der App erhältst du die 100% Passgarantie. Sollten die Maße nicht passen, beheben wir das ohne zusätzliche Kosten.',
                  'By using the app you get a measurement guarantee. If the measurements do not fit, we fix it at no extra cost.'
                )}
              </p>
            </div>
          </div>
        </div>
        <div class="requirements-box">
          <h2 style="text-align: center; font-size: 18px; margin-bottom: 16px;">${t('Det du trenger:', 'Was du benötigst:', 'What you need:')}</h2>
          <ul class="info-list">
            <li><${CheckCircleIcon} /> ${t('Meterstokk, målebånd eller lasermåler', 'Zollstock, Maßband oder Lasermessgerät', 'Folding rule, measuring tape or laser measure')}</li>
            <li><${CheckCircleIcon} /> ${t('En mobiltelefon', 'Ein Smartphone', 'A smartphone')}</li>
            <li><${CheckCircleIcon} /> ${t('5-10 minutter per vindu', '5-10 Minuten pro Fenster', '5-10 minutes per window')}</li>
          </ul>
        </div>
        <button class="button--brand" style="margin-top: 24px; width: 100%;" onClick=${handleStart}>
          ${t('Start nå!', 'Jetzt starten!', 'Start now!')}
        </button>
      </div>
    </div>
  `;

  const renderMeasureToolSelection = () => {
    const tools = [
      {
        id: 'Laser',
        name: t('Lasermåler', 'Lasermessgerät', 'Laser measure'),
        image: config.assets.toolLaser
      },
      {
        id: 'Folding rule',
        name: t('Tommestokk', 'Zollstock', 'Folding rule'),
        image: config.assets.toolZollstock
      },
      {
        id: 'Measuring tape',
        name: t('Målebånd', 'Maßband', 'Measuring tape'),
        image: config.assets.toolMeasuringTape
      },
      {
        id: 'Other',
        name: t('Annet', 'Sonstiges', 'Other'),
        image: null
      }
    ];

    const selectTool = async (toolId) => {
      await saveMeasuringTool(toolId);
      // PDP deep-link visitors go straight into measuring their first window
      // (pass the tool explicitly — the state update above hasn't reached
      // this closure yet). Everyone else keeps the overview in between.
      if (fromPdp) {
        startNewWindow(toolId);
      } else {
        setStep('overview');
      }
    };

    return html`
      <div class="measure-container">
        <div class="measure-header">
          <h1>${t('Hva måler du med?', 'Womit misst du?', 'What are you measuring with?')}</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">
            ${t(
              'Velg verktøyet du bruker. Dette hjelper oss med å kvalitetssikre målene dine.',
              'Wähle das verwendete Werkzeug. Dies hilft uns, die Qualität deiner Maße zu sichern.',
              'Select the tool you are using. This helps us ensure the quality of your measurements.'
            )}
          </p>
        </div>
        <div class="measure-content options-grid">
          ${tools.map(tool => html`
            <div class="option-card ${(!fromPdp && measuringTool === tool.id) ? 'selected' : ''}"
                 style=${tool.id === 'Other' ? 'display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 148px;' : ''}
                 onClick=${() => selectTool(tool.id)}>
              ${tool.image ? html`
                <img src=${tool.image} alt=${tool.name} style="max-height: 120px; object-fit: contain; margin-bottom: 12px; border-radius: 4px;" />
              ` : ''}
              <h3>${tool.name}</h3>
            </div>
          `)}
          <div class="option-card"
               style="grid-column: 1 / -1; display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 96px; border: 2px dashed rgba(91, 91, 249, 0.5); background: rgba(91, 91, 249, 0.04);"
               onClick=${() => { setLaserStatus('idle'); setShowLaserModal(true); }}>
            <h3 style="color: #5b5bf9;">${t('Få GRATIS lasermåler', 'GRATIS Laser erhalten', 'Get a FREE laser measure')}</h3>
            <p style="color: rgba(var(--color-foreground), 0.7); margin: 4px 0 0 0;">
              ${t(
                'Ikke noe verktøy? Vi sender deg en lasermåler gratis.',
                'Kein Werkzeug zur Hand? Wir schicken dir kostenlos ein Lasermessgerät.',
                'No tool at hand? We will send you a laser measure for free.'
              )}
            </p>
          </div>
        </div>
        ${measuringTool ? html`
          <div class="measure-footer" style="display: flex; justify-content: center; margin-top: 24px;">
            <button class="button--brand-secondary" onClick=${() => setStep('overview')}>
              ${t('Avbryt', 'Abbrechen', 'Cancel')}
            </button>
          </div>
        ` : ''}
        ${showLaserModal ? renderFreeLaserModal() : ''}
      </div>
    `;
  };

  const renderOverview = () => {
    return html`
    <div class="measure-container">
      <div class="measure-header" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
        <h1 style="margin: 0;">${t('Dine vinduer', 'Deine Fenster', 'Your windows')}</h1>
        ${hasAccount ? html`
          <span style="font-size: 12px; background: #E5E8F9; color: #5B5BF9; padding: 4px 8px; border-radius: 12px; font-weight: 500;">
            ${t('✓ Lagret på profil', '✓ Im Profil gespeichert', '✓ Saved on profile')}
          </span>
        ` : html`
          <span style="display: inline-flex; align-items: center; gap: 8px; font-size: 12px; color: rgba(var(--color-foreground), 0.6);">
            ${t('Lagret på denne enheten', 'Auf diesem Gerät gespeichert', 'Saved on this device')}
            <a href=${`/customer_authentication/login?return_to=${encodeURIComponent(window.location.pathname)}`}
               style="color: #5B5BF9; font-weight: 600; text-decoration: underline;">
              ${t('Logg inn for å lagre på profilen', 'Anmelden, um im Profil zu speichern', 'Log in to save to your profile')}
            </a>
          </span>
        `}
      </div>
      ${measuringTool ? html`
        <div class="measuring-tool-banner" style="display: flex; align-items: center; justify-content: space-between; background: rgba(var(--color-foreground), 0.03); padding: 6px 16px; border-radius: 8px; margin-top: 16px; font-size: 14px;">
          <div style="display: flex; align-items: center; gap: 12px; color: rgba(var(--color-foreground), 0.8);">
            ${getToolThumbnail(measuringTool) ? html`
              <img src=${getToolThumbnail(measuringTool)} alt="" style="width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; border-radius: 4px; mix-blend-mode: multiply;" />
            ` : ''}
            <span>
              <strong>${t('Måleverktøy:', 'Messwerkzeug:', 'Measuring tool:')}</strong>${' '}
              ${measuringTool === 'Measuring tape' && t('Målebånd', 'Maßband', 'Measuring tape')}
              ${measuringTool === 'Folding rule' && t('Tommestokk', 'Zollstock', 'Folding rule')}
              ${measuringTool === 'Laser' && t('Lasermåler', 'Lasermessgerät', 'Laser measure')}
              ${measuringTool === 'Other' && t('Annet', 'Sonstiges', 'Other')}
            </span>
          </div>
          <button class="text-btn" style="padding: 4px 8px; font-size: 13px;" onClick=${() => setStep('measureTool')}>
            ${t('Endre', 'Ändern', 'Change')}
          </button>
        </div>
      ` : ''}
      <div class="measure-content">
        ${windows.length === 0 ? html`
          <p class="empty-state" style="margin-bottom: 16px;">${t('Kom i gang og legg til ditt første vindu!', 'Füge dein erstes Fenster hinzu, um zu starten!', 'Get started and add your first window!')}</p>
          <div class="guarantee-info-card" style="background: rgba(14, 166, 116, 0.03); border: 1px solid rgba(14, 166, 116, 0.2); border-radius: 12px; padding: 16px; margin-top: 16px; margin-bottom: 24px; display: flex; gap: 12px; align-items: flex-start; text-align: left; width: 100%;">
            <${ShieldCheckIcon} width="20" height="20" style=${{ color: '#0EA674', flexShrink: 0, marginTop: '2px' }} />
            <div style="flex: 1;">
              <h4 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #0EA674;">
                ${t('100% Målegaranti inkludert', '100% Passgarantie inklusive', '100% Fit Guarantee included')}
              </h4>
              <p style="margin: 0; font-size: 15px; color: rgba(var(--color-foreground), 0.7); line-height: 1.5;">
                ${t(
                  'Ved å bruke appen får du 100% målegaranti. Hvis målene ikke stemmer, ordner vi det uten ekstra kostnad.',
                  'Durch die Nutzung der App erhältst du die 100% Passgarantie. Sollten die Maße nicht passen, beheben wir das ohne zusätzliche Kosten.',
                  'By using the app you get a measurement guarantee. If the measurements do not fit, we fix it at no extra cost.'
                )}
              </p>
            </div>
          </div>
          <button class="dashed-add-btn full-width" onClick=${() => startNewWindow()}>
            ${t('+ Legg til vindu', '+ Fenster hinzufügen', '+ Add window')}
          </button>
        ` : html`
          <div class="windows-list">
            ${windows.filter(win => win && win.id).map((win, i) => html`
              <div class="window-card" key=${win.id}>
                <div class="window-card-info">
                  <div class="window-card-image">
                    <img src=${getWindowCardImage(win)} alt="Vindu" />
                  </div>
                  ${getWindowCardDetails(win, i)}
                </div>
                <div class="window-card-actions">
                  <button class="text-btn" onClick=${() => editWindow(win.id)}><${EditIcon} /> ${t('Rediger', 'Bearbeiten', 'Edit')}</button>
                  <button class="text-btn" onClick=${() => duplicateWindow(win.id)}><${CopyIcon} /> ${t('Dupliser', 'Duplizieren', 'Duplicate')}</button>
                  <button class="text-btn text-btn-danger" onClick=${() => removeWindow(win.id)}><${TrashIcon} /> ${t('Fjern', 'Löschen', 'Remove')}</button>
                </div>
              </div>
            `)}
          </div>
          <button class="dashed-add-btn full-width" onClick=${() => startNewWindow()}>
            ${t('+ Legg til et til', '+ Weiteres Fenster hinzufügen', '+ Add another window')}
          </button>
          
          <div class="guarantee-info-card active" style="background: #E6F4EA; border: 1px solid #A3E2AB; border-radius: 12px; padding: 16px; margin-top: 24px; margin-bottom: 0; display: flex; gap: 12px; align-items: flex-start; text-align: left;">
            <${ShieldCheckIcon} width="20" height="20" style=${{ color: '#137333', flexShrink: 0, marginTop: '2px' }} />
            <div style="flex: 1;">
              <h4 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #137333;">
                ${t('100% Målegaranti aktiv', '100% Passgarantie aktiv', '100% Fit Guarantee active')}
              </h4>
              <p style="margin: 0; font-size: 14px; color: #137333; line-height: 1.5;">
                ${t('Dine mål er beskyttet av 100% målegarantien.', 'Deine Maße sind durch die 100% Passgarantie geschützt.', 'Your measurements are protected by the guarantee.')}
              </p>
              <div style="margin-top: 10px;">
                <button class="guarantee-read-more-btn" type="button" style="background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #137333; text-decoration: underline; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;"
                        onClick=${() => setShowGuaranteeModal(true)}>
                  ${t('Les mer', 'Mehr erfahren', 'Read more')}
                </button>
              </div>
            </div>
          </div>
          
          <div class="measure-footer" style="padding: 16px 0 0 0; border: none; background: none; margin-top: 0;">
            <button class="button--brand full-width" style="width: 100%;" onClick=${() => {
              localStorage.setItem('screenlux_transfer_measurements', 'true');
              const returnUrl = customerId ? `/products/zip-screen-configurator?customer_id=${customerId}` : '/products/zip-screen-configurator';
              window.location.href = returnUrl;
            }}>
              ${t('Videre til konfigurator', 'Weiter zum Konfigurator', 'Proceed to configurator')}
            </button>
          </div>
        `}
      </div>
      ${showGuaranteeModal ? renderGuaranteeModal() : ''}
    </div>
  `;
  };

  // V1 - Type Selection
  const renderTypeSelectionV1 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Velg vindustype</h1>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.type === 'Uten ramme' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, type: 'Uten ramme'})}>
          <img src=${config.assets.windowUtenRamme} alt="Vindu uten ramme" />
          <h3>Vindu uten ramme eller vannbord i topp</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Med vannbord' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({...draftWindow, type: 'Med vannbord'})}>
          <img src=${config.assets.windowMedVannbord} alt="Vindu med vannbord" />
          <h3>Vindu med vannbord og/eller omramming</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Skyvedør' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({...draftWindow, type: 'Skyvedør'})}>
          <img src=${config.assets.windowSkyvedor} alt="Skyvedør eller andre typer" />
          <h3>Skyvedør eller andre typer</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.type}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V1 - Mount Selection
  const renderMountSelectionV1 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Innvendig eller utvendig montering?</h1>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.mountType === 'Innvendig' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, mountType: 'Innvendig'})}>
          <img src=${config.assets.zipScreenInni} alt="Montering inni nisje" />
          <h3>Zip-Screen monteres INNI nisje</h3>
        </div>
        <div class="option-card ${draftWindow.mountType === 'Utvendig' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({...draftWindow, mountType: 'Utvendig'})}>
          <img src=${config.assets.zipScreenUtenpaa} alt="Montering utenpå" />
          <h3>Zip-Screen monteres UTENFOR nisje</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.mountType}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V1 - Width Inputs
  const renderWidthV1 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Mål bredden</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) som vist under.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${config.assets.measureWidth} alt="Mål bredde" />
        </div>
        <div class="input-group">
          <label class="field-label">1. Bredde topp (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 3000" value=${draftWindow.widthTop} 
                 onInput=${e => setDraftWindow({...draftWindow, widthTop: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">2. Bredde midten (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 3002" value=${draftWindow.widthMiddle} 
                 onInput=${e => setDraftWindow({...draftWindow, widthMiddle: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">3. Bredde bunn (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 3005" value=${draftWindow.widthBottom} 
                 onInput=${e => setDraftWindow({...draftWindow, widthBottom: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.widthTop || !draftWindow.widthMiddle || !draftWindow.widthBottom}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V1 - Height Inputs
  const renderHeightV1 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Mål høyden</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Mål høyden på to steder (1. venstre, 2. høyre) som vist under.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${config.assets.measureHeight} alt="Mål høyde" />
        </div>
        <div class="input-group">
          <label class="field-label">1. Høyde venstre (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 2000" value=${draftWindow.heightLeft} 
                 onInput=${e => setDraftWindow({...draftWindow, heightLeft: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">2. Høyde høyre (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 2002" value=${draftWindow.heightRight} 
                 onInput=${e => setDraftWindow({...draftWindow, heightRight: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.heightLeft || !draftWindow.heightRight}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // --- VERSION 2 (NO) STEP RENDERERS ---

  // V2 Step: Mount selection (NISJE vs VEGG)
  const renderMountSelectionV2 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Innvendig eller utvendig montering?</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Velg hvordan du planlegger å montere din zip-screen.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.mountType === 'Wall mounted' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, mountType: 'Wall mounted'})}>
          <img src=${config.assets.v3NoOnWall} alt="Montert på vegg" />
          <h3>Montert på vegg</h3>
        </div>
        <div class="option-card ${draftWindow.mountType === 'Recess mounted' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               mountType: 'Recess mounted',
               type: '',
               waterboardDistance: '',
               handleDistance: '',
               hasWaterboard: '',
               hasHandle: ''
             })}>
          <img src=${config.assets.v3NoInsideFrame} alt="Montert i nisje" />
          <h3>Montert i nisje</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.mountType}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V2 Step: Type Selection (only if mounted on vegg)
  const renderTypeSelectionV2 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Velg vindustype</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Velg utførelsen som passer best for vinduet ditt.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.type === 'Window without top waterboard' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               type: 'Window without top waterboard',
               waterboardDistance: '',
               handleDistance: '',
               hasWaterboard: '',
               hasHandle: ''
             })}>
          <img src=${config.assets.v2NoVeggUten} alt="Vindu uten vannbrett" />
          <h3>Vindu uten vannbrett i toppen</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Window with top waterboard' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({
               ...draftWindow, 
               type: 'Window with top waterboard',
               handleDistance: '',
               hasWaterboard: '',
               hasHandle: ''
             })}>
          <img src=${config.assets.v2NoVeggMed} alt="Vindu med vannbrett" />
          <h3>Vindu med vannbrett i toppen</h3>
        </div>
        <div class="option-card ${draftWindow.type === 'Sliding door' ? 'selected' : ''}"
             onClick=${() => setDraftWindow({...draftWindow, type: 'Sliding door'})}>
          <img src=${config.assets.v2NoVeggSkyvedor} alt="Skyvedør" />
          <h3>Skyvedør</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.type}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V2 Step: Sliding Door Waterboard Question
  const renderSlidingDoorWaterboardQ = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Har du vannbrett over skyvedøren?', 'Hast du eine Fensterbank über der Schiebetür?', 'Do you have a waterboard above the sliding door?')}</h1>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Vannbrett spørsmål" />
        </div>
        <div class="options-grid" style="margin-top: 24px;">
          <div class="option-card ${draftWindow.hasWaterboard === 'Yes' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, hasWaterboard: 'Yes'})}>
            <h3 style="text-align: center; margin: 24px 0;">${t('Ja', 'Ja', 'Yes')}</h3>
          </div>
          <div class="option-card ${draftWindow.hasWaterboard === 'No' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, hasWaterboard: 'No'})}>
            <h3 style="text-align: center; margin: 24px 0;">${t('Nei', 'Nein', 'No')}</h3>
          </div>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                disabled=${!draftWindow.hasWaterboard}
                onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
      </div>
    </div>
  `;

  // V2 Step: Sliding Door Handle Question
  const renderSlidingDoorHandleQ = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Har du utstikkende håndtak?', 'Hast du einen überstehenden Griff?', 'Do you have a protruding handle?')}</h1>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Håndtak spørsmål" />
        </div>
        <div class="options-grid" style="margin-top: 24px;">
          <div class="option-card ${draftWindow.hasHandle === 'Yes' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, hasHandle: 'Yes'})}>
            <h3 style="text-align: center; margin: 24px 0;">${t('Ja', 'Ja', 'Yes')}</h3>
          </div>
          <div class="option-card ${draftWindow.hasHandle === 'No' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, hasHandle: 'No'})}>
            <h3 style="text-align: center; margin: 24px 0;">${t('Nei', 'Nein', 'No')}</h3>
          </div>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                disabled=${!draftWindow.hasHandle}
                onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
      </div>
    </div>
  `;


  // V3 Step: Roof Overhang Question
  const renderRoofOverhangQuestion = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Har du takutstikk rett over vinduet?', 'Hast du einen Dachüberstand direkt über dem Fenster?', 'Do you have a roof overhang directly above the window?')}</h1>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Takutstikk spørsmål" />
        </div>
        <div class="options-grid" style="margin-top: 24px;">
          <div class="option-card ${draftWindow.hasRoofOverhang === 'Yes' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, hasRoofOverhang: 'Yes'})}>
            <h3 style="text-align: center; margin: 24px 0;">${t('Ja', 'Ja', 'Yes')}</h3>
          </div>
          <div class="option-card ${draftWindow.hasRoofOverhang === 'No' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, hasRoofOverhang: 'No', roofOverhangDistance: ''})}>
            <h3 style="text-align: center; margin: 24px 0;">${t('Nei', 'Nein', 'No')}</h3>
          </div>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                disabled=${!draftWindow.hasRoofOverhang}
                onClick=${nextStep}>${t('Neste', 'Weiter', 'Next')}</button>
      </div>
    </div>
  `;

  // V2 Step: Width Inputs
  const renderWidthV2 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Mål bredden</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) som vist under.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Mål bredde" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">1. Bredde topp (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 3000" value=${draftWindow.widthTop} 
                 onInput=${e => setDraftWindow({...draftWindow, widthTop: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">2. Bredde midten (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 3002" value=${draftWindow.widthMiddle} 
                 onInput=${e => setDraftWindow({...draftWindow, widthMiddle: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">3. Bredde bunn (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 3005" value=${draftWindow.widthBottom} 
                 onInput=${e => setDraftWindow({...draftWindow, widthBottom: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.widthTop || !draftWindow.widthMiddle || !draftWindow.widthBottom}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V2 Step: Height Inputs
  const renderHeightV2 = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Mål høyden</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Mål høyden på to steder (1. venstre, 2. høyre) som vist under.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Mål høyde" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">1. Høyde venstre (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 2100" value=${draftWindow.heightLeft} 
                 onInput=${e => setDraftWindow({...draftWindow, heightLeft: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">2. Høyde høyre (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 2100" value=${draftWindow.heightRight} 
                 onInput=${e => setDraftWindow({...draftWindow, heightRight: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.heightLeft || !draftWindow.heightRight}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V2 Step: Waterboard Distance
  const renderWaterboardDistance = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Avstand fra vegg til ytterkant vannbord</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Mål utstikk på vannbordet i millimeter (mm).</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Mål avstand vannbrett" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">Avstand (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 80" value=${draftWindow.waterboardDistance} 
                 onInput=${e => setDraftWindow({...draftWindow, waterboardDistance: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.waterboardDistance}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // V2 Step: Handle Distance
  const renderHandleDistance = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Avstand fra vegg til ytterkant håndtak</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Mål hvor langt håndtaket stikker ut fra veggen i millimeter (mm).</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${getStepImage()} alt="Mål avstand håndtak" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">Avstand (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="F. eks 45" value=${draftWindow.handleDistance} 
                 onInput=${e => setDraftWindow({...draftWindow, handleDistance: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Tilbake</button>
        <button class="button--brand" 
                disabled=${!draftWindow.handleDistance}
                onClick=${nextStep}>Neste</button>
      </div>
    </div>
  `;

  // --- GERMAN VERSION STEPS ---

  const renderMountSelectionDE = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Montageart auswählen</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Wähle, wie du deinen Zipscreen montieren möchtest.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.mountType === 'Recess mounted' && !draftWindow.revealDepth ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({
               ...draftWindow,
               mountType: 'Recess mounted',
               fasteningType: '',
               revealDepth: '',
               screwingHindered: ''
             })}>
          <img src=${config.assets.v2DeInDerLaibung} alt="In der Laibung" />
          <h3>In der Laibung</h3>
        </div>
        <div class="option-card ${draftWindow.mountType === 'Wall mounted' && !draftWindow.revealDepth ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({
               ...draftWindow,
               mountType: 'Wall mounted',
               fasteningType: '',
               revealDepth: '',
               screwingHindered: ''
             })}>
          <img src=${config.assets.v2DeAnDieWand} alt="An die Wand" />
          <h3>An die Wand</h3>
        </div>
        <div class="option-card ${draftWindow.mountType === 'Unknown' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({
               ...draftWindow,
               mountType: 'Unknown',
               fasteningType: '',
               revealDepth: '',
               screwingHindered: ''
             })}>
          <div style="height: 150px; display: flex; align-items: center; justify-content: center; background: rgba(var(--color-foreground), 0.02); border-radius: 8px;">
            <span style="font-size: 48px; color: #5B5BF9;">?</span>
          </div>
          <h3>Ich weiß nicht</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
        <button class="button--brand" 
                disabled=${!draftWindow.mountType}
                onClick=${nextStep}>Weiter</button>
      </div>
    </div>
  `;

  const renderRevealDepthQ = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Tiefe der Laibung messen</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Miss den Abstand von der Außenkante der Wand bis zum Fensterrahmen in Millimetern (mm).</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${config.assets.v2DeTiefeDerLaibung} alt="Laibungstiefe messen" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">Tiefe (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="z. B. 50" value=${draftWindow.revealDepth || ''} 
                 onInput=${e => setDraftWindow({...draftWindow, revealDepth: e.target.value})} />
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
        <button class="button--brand" 
                disabled=${!draftWindow.revealDepth}
                onClick=${nextStep}>Weiter</button>
      </div>
    </div>
  `;

  const renderRevealDepthRecommendation = () => {
    const depth = parseInt(draftWindow.revealDepth, 10) || 0;
    const recommendRecess = depth >= 40;
    
    return html`
      <div class="measure-container">
        <div class="measure-header">
          <h1>Unsere Empfehlung</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Basierend auf deiner Laibungstiefe von ${depth} mm empfehlen wir folgende Montageart:</p>
        </div>
        ${renderProgressBar()}
        <div class="measure-content" style="text-align: center;">
          <div class="instruction-image" style="max-height: 250px; margin-bottom: 20px;">
            <img src=${recommendRecess ? config.assets.v2DeInDerLaibung : config.assets.v2DeAnDieWand} alt="Montageempfehlung" style="max-height: 250px; object-fit: contain;" />
          </div>
          <div style="background: rgba(91, 91, 249, 0.05); border: 1px solid rgba(91, 91, 249, 0.15); padding: 16px; border-radius: 8px; margin-top: 16px;">
            <h2 style="color: #5B5BF9; margin: 0 0 8px 0; font-size: 20px;">
              ${recommendRecess ? 'Montage in der Laibung' : 'Montage an die Wand'}
            </h2>
            <p style="margin: 0; font-size: 15px; line-height: 1.5; color: rgba(var(--color-foreground), 0.8);">
              ${recommendRecess 
                ? 'Da deine Laibungstiefe 4 cm (40 mm) oder mehr beträgt, ist genügend Platz vorhanden, um den Zipscreen elegant in der Laibung zu montieren.' 
                : 'Da deine Laibungstiefe weniger als 4 cm (40 mm) beträgt, empfehlen wir die Montage an die Wand (auf dem Mauerwerk), da der Platz in der Laibung für die Führungsschienen nicht ausreicht.'}
            </p>
          </div>
        </div>
        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
          <button class="button--brand" onClick=${nextStep}>Weiter</button>
        </div>
      </div>
    `;
  };

  const renderFasteningTypeDE = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Befestigungsart wählen</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Wähle, wie der Zipscreen befestigt werden soll.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content options-grid">
        <div class="option-card ${draftWindow.fasteningType === 'In the window frame' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, fasteningType: 'In the window frame', screwingHindered: ''})}>
          <img src=${config.assets.v2DeInDenFensterrahmen} alt="In den Fensterrahmen" />
          <h3>In den Fensterrahmen</h3>
        </div>
        <div class="option-card ${draftWindow.fasteningType === 'Sideways in the wall' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, fasteningType: 'Sideways in the wall', screwingHindered: ''})}>
          <img src=${config.assets.v2DeSeitlichInDieWand} alt="Seitlich in die Wand" />
          <h3>Seitlich in die Wand (Laibung)</h3>
        </div>
        <div class="option-card ${draftWindow.fasteningType === 'Unknown' ? 'selected' : ''}" 
             onClick=${() => setDraftWindow({...draftWindow, fasteningType: 'Unknown', screwingHindered: ''})}>
          <div style="height: 150px; display: flex; align-items: center; justify-content: center; background: rgba(var(--color-foreground), 0.02); border-radius: 8px;">
            <span style="font-size: 48px; color: #5B5BF9;">?</span>
          </div>
          <h3>Ich weiß nicht</h3>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
        <button class="button--brand" 
                disabled=${!draftWindow.fasteningType}
                onClick=${nextStep}>Weiter</button>
      </div>
    </div>
  `;

  const renderScrewingHinderedQ = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Fensterrahmen frei von Hindernissen?</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Hast du Rollläden oder sonstige Hindernisse, die ein direktes Anschrauben in den Fensterrahmen verhindern?</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="options-grid" style="margin-top: 24px;">
          <div class="option-card ${draftWindow.screwingHindered === 'No' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, screwingHindered: 'No'})}>
            <h3 style="text-align: center; margin: 40px 0;">Nein, alles frei</h3>
          </div>
          <div class="option-card ${draftWindow.screwingHindered === 'Yes' ? 'selected' : ''}" 
               onClick=${() => setDraftWindow({...draftWindow, screwingHindered: 'Yes'})}>
            <h3 style="text-align: center; margin: 40px 0;">Ja, es gibt Hindernisse</h3>
          </div>
        </div>
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
        <button class="button--brand" 
                disabled=${!draftWindow.screwingHindered}
                onClick=${nextStep}>Weiter</button>
      </div>
    </div>
  `;

  const renderScrewingHinderedRecommendation = () => {
    const noObstacles = draftWindow.screwingHindered === 'No';
    
    return html`
      <div class="measure-container">
        <div class="measure-header">
          <h1>Unsere Empfehlung</h1>
          <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Basierend auf deinen Angaben empfehlen wir folgende Befestigungsart:</p>
        </div>
        ${renderProgressBar()}
        <div class="measure-content" style="text-align: center;">
          <div class="instruction-image" style="max-height: 250px; margin-bottom: 20px;">
            <img src=${noObstacles ? config.assets.v2DeInDenFensterrahmen : config.assets.v2DeSeitlichInDieWand} alt="Befestigungsempfehlung" style="max-height: 250px; object-fit: contain;" />
          </div>
          <div style="background: rgba(91, 91, 249, 0.05); border: 1px solid rgba(91, 91, 249, 0.15); padding: 16px; border-radius: 8px; margin-top: 16px;">
            <h2 style="color: #5B5BF9; margin: 0 0 8px 0; font-size: 20px;">
              ${noObstacles ? 'Befestigung im Fensterrahmen' : 'Seitliche Befestigung in der Wand'}
            </h2>
            <p style="margin: 0; font-size: 15px; line-height: 1.5; color: rgba(var(--color-foreground), 0.8);">
              ${noObstacles 
                ? 'Da dein Fensterrahmen frei von Hindernissen ist, empfehlen wir die Befestigung direkt im Fensterrahmen. Dies ist die stabilste und optisch unauffälligste Variante.' 
                : 'Aufgrund der vorhandenen Rollläden oder Hindernisse empfehlen wir die seitliche Befestigung in die Wand (Laibung). So bleibt die Funktion der Rollläden unbeeinträchtigt.'}
            </p>
          </div>
        </div>
        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
          <button class="button--brand" onClick=${nextStep}>Weiter</button>
        </div>
      </div>
    `;
  };

  const renderWidthDE = () => {
    const widthTooSmall = [draftWindow.widthTop, draftWindow.widthMiddle, draftWindow.widthBottom]
      .some(v => v !== undefined && v !== '' && Number(v) < MIN_WIDTH_MM);
    return html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Breite messen</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Miss die Breite an 3 Stellen (oben, mitte, unten) wie in der Zeichnung gezeigt.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${config.assets.v2DeWidth} alt="Breite messen" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">Oben (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="z. B. 3000" value=${draftWindow.widthTop}
                 onInput=${e => setDraftWindow({...draftWindow, widthTop: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">Mitte (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="z. B. 3002" value=${draftWindow.widthMiddle}
                 onInput=${e => setDraftWindow({...draftWindow, widthMiddle: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">Unten (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="z. B. 3005" value=${draftWindow.widthBottom}
                 onInput=${e => setDraftWindow({...draftWindow, widthBottom: e.target.value})} />
        </div>
        ${widthTooSmall ? html`
          <div class="warning-banner-inline" style="margin-top: 12px; padding: 8px 12px; background: #FEF2F2; border-left: 4px solid #EF4444; border-radius: 4px; font-size: 13px; color: #991B1B; line-height: 1.4;">
            <strong>⚠️ Warnung:</strong> Unsere Mindestbreite beträgt ${MIN_WIDTH_MM} mm. Bitte prüfe deine Messung.
          </div>
        ` : ''}
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
        <button class="button--brand"
                disabled=${!draftWindow.widthTop || !draftWindow.widthMiddle || !draftWindow.widthBottom}
                onClick=${nextStep}>Weiter</button>
      </div>
    </div>
  `;
  };

  const renderHeightDE = () => {
    const heightTooSmall = [draftWindow.heightLeft, draftWindow.heightRight]
      .some(v => v !== undefined && v !== '' && Number(v) < MIN_HEIGHT_MM);
    return html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>Höhe messen</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">Miss die Höhe an 2 Stellen (links, rechts) wie in der Zeichnung gezeigt.</p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="instruction-image">
          <img src=${config.assets.v2DeHeight} alt="Höhe messen" />
        </div>
        <div class="input-group" style="margin-top: 24px;">
          <label class="field-label">Links (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="z. B. 2000" value=${draftWindow.heightLeft}
                 onInput=${e => setDraftWindow({...draftWindow, heightLeft: e.target.value})} />
        </div>
        <div class="input-group">
          <label class="field-label">Rechts (mm)</label>
          <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" placeholder="z. B. 2000" value=${draftWindow.heightRight}
                 onInput=${e => setDraftWindow({...draftWindow, heightRight: e.target.value})} />
        </div>
        ${heightTooSmall ? html`
          <div class="warning-banner-inline" style="margin-top: 12px; padding: 8px 12px; background: #FEF2F2; border-left: 4px solid #EF4444; border-radius: 4px; font-size: 13px; color: #991B1B; line-height: 1.4;">
            <strong>⚠️ Warnung:</strong> Unsere Mindesthöhe beträgt ${MIN_HEIGHT_MM} mm. Bitte prüfe deine Messung.
          </div>
        ` : ''}
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>Zurück</button>
        <button class="button--brand"
                disabled=${!draftWindow.heightLeft || !draftWindow.heightRight}
                onClick=${nextStep}>Weiter</button>
      </div>
    </div>
  `;
  };

  // --- COMMON STEPS ---

  const saveCurrentDraft = () => {
    let updatedWindows = [...windows];
    const existingIndex = updatedWindows.findIndex(w => w.id === draftWindow.id);
    const updatedDraft = { ...draftWindow, measurementGuarantee: getWindowOverhangObstruction(draftWindow) ? false : true };
    
    if (existingIndex >= 0) {
      updatedWindows[existingIndex] = updatedDraft;
    } else {
      updatedWindows.push(updatedDraft);
    }
    
    saveToStorage(updatedWindows);
    
    // Funnel: user finished a window (once per session), for everyone.
    trackFunnelEvent('window_completed');

    if (forceLogin) {
      // saveToStorage above has already written to localStorage, so abandoning
      // the login costs the measurements nothing — coming back lands on the
      // same windows. login_prompted measures the drop-off this wall creates;
      // it fires only where the wall exists.
      trackFunnelEvent('login_prompted');
      try {
        sessionStorage.setItem('login_redirect', window.location.href);
      } catch (e) {
        console.error('Failed to store login redirect', e);
      }
      window.location.href = `/customer_authentication/login?return_to=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    setStep('overview');
  };

  const renderName = () => html`
    <div class="measure-container">
      <div class="measure-header">
        <h1>${t('Gi vinduet ditt et navn', 'Gib deinem Fenster einen Namen', 'Give your window a name')}</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px;">
          ${t('Lag et beskrivende navn og last gjerne opp et bilde.', 'Gib einen beschreibenden Namen ein und lade optional ein Foto hoch.', 'Name this window and optionally upload a photo.')}
        </p>
      </div>
      ${renderProgressBar()}
      <div class="measure-content">
        <div class="input-group">
          <label class="field-label">${t('Navn (valgfritt)', 'Name (optional)', 'Name (optional)')}</label>
          <input class="sl-input" type="text" placeholder=${t('F.eks. Stue høyre', 'z. B. Küche links', 'e.g. Kitchen left')} value=${draftWindow.name}
                 maxlength=${MAX_NAME_LENGTH}
                 onInput=${e => setDraftWindow({...draftWindow, name: capName(e.target.value)})} />
          <p style="margin: 6px 0 0 0; font-size: 13px; color: rgba(var(--color-foreground), 0.55);">
            ${(draftWindow.name || '').length} / ${MAX_NAME_LENGTH}
          </p>
        </div>
        
        <div class="input-group" style="margin-top: 32px;">
          <label class="field-label">${t('Bilde (valgfritt)', 'Foto (optional)', 'Photo (optional)')}</label>
          <label class="image-upload-area" style="display: flex; cursor: pointer;">
            <input type="file" accept="image/*" style="display: none;" onChange=${handleImageUpload} />
            ${draftWindow.image ? html`
              <img src=${draftWindow.image} style="max-height: 150px; border-radius: 8px; object-fit: contain;" alt=${t('Vindusbilde', 'Fensterbild', 'Window image')} />
            ` : html`
              <${UploadIcon} />
              <p>${t('Klikk her for å ta bilde eller laste opp', 'Klicke hier, um ein Foto aufzunehmen oder hochzuladen', 'Click here to take a photo or upload')}</p>
            `}
          </label>
        </div>

        ${!hasAccount ? html`
          <div style="margin-top: 24px; padding: 12px 16px; background: rgba(91, 91, 249, 0.05); border: 1px solid rgba(91, 91, 249, 0.15); border-radius: 8px; font-size: 14px; color: rgba(var(--color-foreground), 0.8); line-height: 1.4;">
            ${forceLogin
              ? t(
                  'Etter at du har logget inn, vil vinduet lagres på profilen din slik at du kan legge til flere vinduer.',
                  'Nach dem Einloggen wird das Fenster in deinem Profil gespeichert, sodass du weitere Fenster hinzufügen kannst.',
                  'After logging in, the window will be saved to your profile so you can add more windows.'
                )
              : t(
                  'Målene lagres på denne enheten, så du kan fortsette med en gang. Vil du lagre dem på profilen din og fortsette på en annen enhet, kan du logge inn senere.',
                  'Deine Maße werden auf diesem Gerät gespeichert, du kannst also direkt weitermachen. Wenn du sie in deinem Profil speichern und auf einem anderen Gerät fortsetzen willst, kannst du dich später anmelden.',
                  'Your measurements are saved on this device, so you can continue right away. To store them in your profile and continue on another device, you can log in later.'
                )}
          </div>
        ` : ''}
      </div>
      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${prevStep}>${t('Tilbake', 'Zurück', 'Back')}</button>
        <button class="button--brand" 
                onClick=${saveCurrentDraft} disabled=${isSaving}>
          ${isSaving 
            ? t('Lagrer...', 'Speichert...', 'Saving...') 
            : (forceLogin
                ? t('Lagre & Logg inn', 'Speichern & Einloggen', 'Save & Log in')
                : t('Lagre målene', 'Maße speichern', 'Save measurements'))
          }
        </button>
      </div>
    </div>
  `;

  const renderProgressBar = () => {
    if (['start', 'overview'].includes(step)) return null;
    return html`
      <div class="measure-progress-bar" style="width: 100%; height: 6px; background: #E5E8F9; border-radius: 3px; overflow: hidden; margin-bottom: 24px;">
        <div class="measure-progress-fill" 
             ref=${el => { if (el) { el.style.width = getProgress() + '%'; el.style.height = '100%'; el.style.backgroundColor = '#5B5BF9'; el.style.transition = 'width 0.3s ease'; } }}>
        </div>
      </div>
    `;
  };

  const renderCurrentStep = () => {
    switch(step) {
      case 'start': return renderStart();
      case 'overview': return renderOverview();
      case 'name': return renderName();
      case 'measureTool': return renderMeasureToolSelection();
      
      // German-specific steps
      case 'revealDepthQ':
        return renderRevealDepthQ();
      case 'revealDepthRecommendation':
        return renderRevealDepthRecommendation();
      case 'fasteningType':
        return renderFasteningTypeDE();
      case 'screwingHinderedQ':
        return renderScrewingHinderedQ();
      case 'screwingHinderedRecommendation':
        return renderScrewingHinderedRecommendation();
      
      case 'flowSelection':
        return renderFlowSelection();
      case 'aiUpload':
        return renderAiUpload();
      case 'aiScanning':
        return renderAiScanning();
      case 'aiConfirm':
        return renderAiConfirm();

      // V2 Steps (Norwegian / German)
      case 'mount':
        if (isDE) return renderMountSelectionDE();
        return isNO ? renderMountSelectionV2() : renderMountSelectionV1();
      case 'type':
        return isNO ? renderTypeSelectionV3() : renderTypeSelectionV1();
      case 'slidingDoorWaterboardQ':
        return renderSlidingDoorWaterboardQ();
      case 'slidingDoorHandleQ':
        return renderSlidingDoorHandleQ();
      case 'width':
        if (isDE) return renderWidthDE();
        return isNO ? renderWidthV2() : renderWidthV1();
      case 'height':
        if (isDE) return renderHeightDE();
        return isNO ? renderHeightV2() : renderHeightV1();
      case 'waterboardDistance':
        return renderWaterboardDistance();
      case 'handleDistance':
        return renderHandleDistance();
        
      // V3 NO Steps
      case 'inside-frame_width':
        return renderWidthStepGeneric(
          t('Mål bredden i nisjen', 'Breite in der Nische messen', 'Measure width in the recess'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra venstre til høyre side inni nisjen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von der linken zur rechten Seite in der Nische.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from the left to the right side inside the recess.')
        );
      case 'inside-frame_height':
        return renderHeightStepGeneric(
          t('Mål høyden i nisjen', 'Höhe in der Nische messen', 'Measure height in the recess'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant til overkant inni nisjen.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante zur Oberkante in der Nische.', 'Measure the height in two places (1. left, 2. right) from the bottom edge to the top edge inside the recess.')
        );
      
      case 'on-wall_without-waterboard-on-top_width':
        return renderWidthStepGeneric(
          t('Mål bredden på vinduet', 'Breite des Fensters messen', 'Measure window width'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra ytterkant til ytterkant av vindusrammen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von Außenkante zu Außenkante des Fensterrahmens.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from outer edge to outer edge of the window frame.')
        );
      case 'on-wall_without-waterboard-on-top_height':
        return renderHeightStepGeneric(
          t('Mål høyden på vinduet', 'Höhe des Fensters messen', 'Measure window height'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av vindusrammen til overkant av vindusrammen.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante des Fensterrahmens zur Oberkante des Fensterrahmens.', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the window frame to the top edge of the window frame.')
        );
        
      case 'on-wall_with-waterboard-on-top_window-opening-out':
        return renderWindowOpeningOutQ();
      case 'on-wall_with-waterboard-on-top_heigth-from-top-window-to-waterboard':
        return renderSingleInputStepGeneric(
          t('Mål avstand fra vindu til vannbrett', 'Abstand vom Fenster zur Fensterbank messen', 'Measure distance from window to waterboard'), 
          t('Mål avstanden fra toppen av vindusrammen til undersiden av vannbrettet over vinduet.', 'Miss den Abstand von der Oberkante des Fensterrahmens zur Unterseite der Fensterbank über dem Fenster.', 'Measure the distance from the top of the window frame to the underside of the waterboard above the window.'), 
          'topToWaterboardHeight', 'F. eks 100'
        );
      case 'on-wall_with-waterboard-on-top_width':
        return renderWidthStepGeneric(
          t('Mål bredden på vinduet', 'Breite des Fensters messen', 'Measure window width'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra ytterkant til ytterkant av vindusrammen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von Außenkante zu Außenkante des Fensterrahmens.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from outer edge to outer edge of the window frame.')
        );
      case 'on-wall_with-waterboard-on-top_height-to-top-window':
        return renderHeightStepGeneric(
          t('Mål høyden til toppen av vindusrammen', 'Höhe bis zur Oberkante des Fensterrahmens messen', 'Measure height to the top of the window frame'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av vindusrammen opp til overkant av vindusrammen (toppen av karmen).', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante des Fensterrahmens bis zur Oberkante des Fensterrahmens (oben am Rahmen).', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the window frame up to the top edge of the window frame (top of the frame).')
        );
      case 'on-wall_with-waterboard-on-top_height-to-waterboard':
        return renderHeightStepGeneric(
          t('Mål høyden opp til vannbrettet', 'Höhe bis zur Fensterbank messen', 'Measure height to the waterboard'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av vinduet opp til underkant av vannbrettet over vinduet.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante des Fensters bis zur Unterseite der Fensterbank über dem Fenster.', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the window up to the underside of the waterboard above the window.')
        );
      case 'on-wall_waterboard-out-from-wall':
        return renderSingleInputStepGeneric(
          t('Hvor langt stikker vannbrettet ut?', 'Wie weit steht die Fensterbank über?', 'How far does the waterboard protrude?'), 
          t('Mål utstikk på vannbrettet fra ytterveggen i millimeter (mm).', 'Miss den Überstand der Fensterbank von der Außenwand in Millimetern (mm).', 'Measure the protrusion of the waterboard from the outer wall in millimeters (mm).'), 
          'waterboardDistance', 'F. eks 80'
        );
        
      case 'on-wall_waterboard-question':
        return renderSlidingDoorWaterboardQ();
      case 'on-wall_sticking-out-handle-question':
        return renderSlidingDoorHandleQ();
      case 'on-wall_roof-overhang-question':
        return renderRoofOverhangQuestion();
      case 'on-wall_roof-overhang-measure':
        return renderSingleInputStepGeneric(
          t('Mål avstanden til takutstikket', 'Abstand zum Dachüberstand messen', 'Measure distance to the roof overhang'), 
          t('Mål avstanden fra overkanten av vindusåpningen/karmen og opp til takutstikket i millimeter (mm).', 'Miss den Abstand von der Oberkante der Fensteröffnung/des Rahmens bis zum Dachüberstand in Millimetern (mm).', 'Measure the distance from the top of the window opening/frame up to the roof overhang in millimeters (mm).'), 
          'roofOverhangDistance', 'F. eks 100'
        );
        
      case 'on-wall_sliding-door_with-waterboard-width':
        return renderWidthStepGeneric(
          t('Mål bredde med vannbrett', 'Breite der Schiebetür messen', 'Measure sliding door width'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra ytterkant til ytterkant av dørkarmen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von Außenkante zu Außenkante des Schiebetürrahmens.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from outer edge to outer edge of the sliding door frame.')
        );
      case 'on-wall_sliding-door_with-waterboard-heigth':
        return renderHeightStepGeneric(
          t('Mål høyde til vannbrett', 'Höhe bis zur Fensterbank messen', 'Measure height to the waterboard'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av døren (terskelen) opp til undersiden av vannbrettet over skyvedøren.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante der Tür (Schwelle) bis zur Unterseite der Fensterbank über der Schiebetür.', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the door (sill) up to the underside of the waterboard above the sliding door.')
        );
      case 'on-wall_sliding-door_without-waterboard-width':
        return renderWidthStepGeneric(
          t('Mål bredde uten vannbrett', 'Breite der Schiebetür messen', 'Measure sliding door width'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra ytterkant til ytterkant av dørkarmen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von Außenkante zu Außenkante des Schiebetürrahmens.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from outer edge to outer edge of the sliding door frame.')
        );
      case 'on-wall_sliding-door_without-waterboard-heigth':
        return renderHeightStepGeneric(
          t('Mål høyde uten vannbrett', 'Höhe der Schiebetür messen', 'Measure sliding door height'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av døren (terskelen) til overkant av skyvedørskarmen.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante der Tür (Schwelle) zur Oberkante des Schiebetürrahmens.', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the door (sill) to the top edge of the sliding door frame.')
        );
      case 'on-wall_sticking-out-handle-measure':
        return renderSingleInputStepGeneric(
          t('Hvor mye stikker håndtaket ut?', 'Wie weit steht der Griff ab?', 'How far does the handle protrude?'), 
          t('Mål hvor mange millimeter håndtaket stikker ut fra ytterveggen.', 'Miss, wie viele Millimeter der Griff von der Außenwand absteht.', 'Measure how many millimeters the handle protrudes from the outer wall.'), 
          'handleDistance', 'F. eks 45'
        );
        
      case 'on-wall_terrace-door_open-direction':
        return renderTerraceDoorOpenDirectionQ();
        
      case 'on-wall_sliding-door_without-waterboard-width-1':
        return renderWidthStepGeneric(
          t('Mål bredden på terrassedøren', 'Breite der Terrassentür messen', 'Measure terrace door width'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra ytterkant til ytterkant av dørkarmen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von Außenkante zu Außenkante des Türrahmens.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from outer edge to outer edge of the door frame.')
        );
      case 'on-wall_sliding-door_without-waterboard-heigth-1':
        return renderHeightStepGeneric(
          t('Mål høyden på terrassedøren', 'Höhe der Terrassentür messen', 'Measure terrace door height'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av døren (terskelen) til overkant av dørkarmen.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante der Tür (Schwelle) zur Oberkante des Türrahmens.', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the door (sill) to the top edge of the door frame.')
        );
        
      case 'on-wall_sliding-door_skyvedor-with-vanbrett-width':
        return renderWidthStepGeneric(
          t('Mål bredden på terrassedøren', 'Breite der Terrassentür messen', 'Measure terrace door width'), 
          t('Mål bredden på tre steder (1. topp, 2. midten, 3. bunn) fra ytterkant til ytterkant av dørkarmen.', 'Miss die Breite an drei Stellen (1. oben, 2. mitte, 3. unten) von Außenkante zu Außenkante des Türrahmens.', 'Measure the width in three places (1. top, 2. middle, 3. bottom) from outer edge to outer edge of the door frame.')
        );
      case 'on-wall_sliding-door_skyvedor-with-vanbrett-height-to-waterboard':
        return renderHeightStepGeneric(
          t('Mål høyden opp til vannbrettet', 'Höhe bis zur Fensterbank messen', 'Measure height to the waterboard'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av døren (terskelen) opp til undersiden av vannbrettet over terrassedøren.', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante der Tür (Schwelle) bis zur Unterseite der Fensterbank über der Terrassentür.', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the door (sill) up to the underside of the waterboard above the terrace door.')
        );
      case 'on-wall_sliding-door_waterboard-measure':
        return renderSingleInputStepGeneric(
          t('Mål avstand fra dør til vannbrett', 'Abstand von der Tür zur Fensterbank messen', 'Measure distance from door to waterboard'), 
          t('Mål avstanden fra toppen av dørkarmen til undersiden av vannbrettet over terrassedøren.', 'Miss den Abstand von der Oberkante des Türrahmens zur Unterseite der Fensterbank über der Terrassentür.', 'Measure the distance from the top of the door frame to the underside of the waterboard above the terrace door.'), 
          'topToWaterboardHeight', 'F. eks 100'
        );
      case 'on-wall_sliding-door_skyvedor-with-vanbrett-height-to-top-door':
        return renderHeightStepGeneric(
          t('Mål høyden til toppen av døren', 'Höhe bis zur Oberkante der Tür messen', 'Measure height to the top of the door'), 
          t('Mål høyden på to steder (1. venstre, 2. høyre) fra underkant av døren (terskelen) opp til overkant av dørkarmen (toppen av dørkarmen).', 'Miss die Höhe an zwei Stellen (1. links, 2. rechts) von der Unterkante der Tür (Schwelle) bis zur Oberkante des Türrahmens (oben am Türrahmen).', 'Measure the height in two places (1. left, 2. right) from the bottom edge of the door (sill) up to the top edge of the door frame (top of the door frame).')
        );
        
      default: return renderStart();
    }
  };

  const renderDesktopQR = () => {
    // Only display on desktop
    const qrUrl = window.location.origin + `/pages/measure-app` + (customerId ? `?customer_id=${customerId}` : '');
    const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}`;
    const returnPath = customerId ? `/pages/measure-app?customer_id=${customerId}` : '/pages/measure-app';
    const loginUrl = `/customer_authentication/login?return_to=${encodeURIComponent(returnPath)}`;

    if (hasAccount) {
      return html`
        <div class="desktop-qr-container" style="
          display: flex;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 20px 24px;
          margin-bottom: 30px;
          gap: 20px;
          align-items: center;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
        ">
          <div style="background: #ffffff; padding: 8px; border: 1px solid rgba(0,0,0,0.08); border-radius: 8px; display: flex; flex-shrink: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.02);">
            <img src="${qrImg}" alt="QR Code" width="130" height="130" style="display: block;" />
          </div>
          <div style="flex-grow: 1; text-align: left;">
            <h3 style="margin: 0 0 6px 0; font-size: 17px; font-weight: 700; color: #111827;">
              ${t('Mål opp vinduene dine med mobilen 📱', 'Fenster einfach mit dem Handy ausmessen 📱', 'Measure your windows with your phone 📱')}
            </h3>
            <p style="margin: 0; font-size: 13.5px; color: #4b5563; line-height: 1.5;">
              ${t(
                'Skann QR-koden for å åpne måleguiden på telefonen. Vinduer du legger til på mobilen vil vises på denne PC-skjermen live i sanntid.',
                'Scanne den QR-Code, um die Mess-App auf deinem Smartphone zu öffnen. Neue Fenster werden live auf diesem Bildschirm synchronisiert.',
                'Scan the QR code to open the measure guide on your phone. Windows added on your phone will display on this screen live in real-time.'
              )}
            </p>
          </div>
        </div>
        <style>
          @media (max-width: 749px) {
            .desktop-qr-container {
              display: none !important;
            }
          }
        </style>
      `;
    } else {
      return html`
        <div class="desktop-qr-container" style="
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 20px 24px;
          margin-bottom: 30px;
          gap: 16px;
          text-align: left;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
        ">
          <div>
            <h3 style="margin: 0 0 6px 0; font-size: 17px; font-weight: 700; color: #111827;">
              ${t('Vil du koble PC og mobil? 🔗', 'PC und Smartphone verbinden? 🔗', 'Connect PC and Phone? 🔗')}
            </h3>
            <p style="margin: 0; font-size: 13.5px; color: #4b5563; line-height: 1.5;">
              ${t(
                'Logg inn først slik at målene du gjør på mobilen automatisk vises her på PC-skjermen din i sanntid.',
                'Melde dich zuerst an, damit deine Messungen auf dem Smartphone automatisch live auf diesem PC-Bildschirm erscheinen.',
                'Log in first so that your measurements on your phone automatically appear here on your PC screen in real-time.'
              )}
            </p>
          </div>
          <div>
            <a href="${loginUrl}" class="button--brand" style="
              display: inline-flex;
              align-items: center;
              justify-content: center;
              padding: 10px 20px;
              font-size: 14px;
              font-weight: 600;
              border-radius: 8px;
              text-decoration: none;
              color: #ffffff;
              background-color: #5B5BF9;
              cursor: pointer;
            ">
              ${t('Logg inn her', 'Hier einloggen', 'Log in here')}
            </a>
          </div>
        </div>
        <style>
          @media (max-width: 749px) {
            .desktop-qr-container {
              display: none !important;
            }
          }
        </style>
      `;
    }
  };

  // Small in-flow "need help?" link, rendered once right after every step's
  // own content (not per-step — see the single call site below) so it always
  // lands close to the Zurück/Weiter buttons without needing 34 separate
  // edits across every render*Step function in this file.
  const renderHelpLink = () => html`
    <div style="text-align: center; margin-top: 16px;">
      <button type="button" onClick=${() => setShowHelpModal(true)}
              style="background: none; border: none; padding: 6px 8px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px;">
        ${renderSupportAvatar(22)}
        <span style="display: flex; flex-direction: column; align-items: flex-start; line-height: 1.3;">
          <span style="color: #5B5BF9; font-weight: 600; font-size: 14px;">${t('Trenger du hjelp?', 'Brauchst du Hilfe?', 'Need help?')}</span>
          <span style="color: #137333; font-weight: 600; font-size: 11px;">${t('Vi er online', 'Wir sind online', "We're online")}</span>
        </span>
      </button>
    </div>
  `;

  // Main Render Switch
  return html`
    <div class="measure-app-wrapper">
      ${renderDesktopQR()}
      <div key=${step} class="measure-step-animate">
        ${renderCurrentStep()}
        ${renderHelpLink()}
      </div>
      ${showHelpModal ? renderHelpModal() : ''}
    </div>
  `;
};

// Mount the App
const rootNode = document.getElementById('measure-app-root');
if (rootNode) {
  render(html`<${MeasureApp} />`, rootNode);
}
